import { Accessor, Loader, Metadata, Event, InvalidAccessorDefinitionError, I18n, Validation, FormatterParserProvider, Mask, DatasourceFactory, Interceptor, Config, Cookie, Router, Http } from '@zeedhi/core';
import Prism__default from 'prismjs';
import 'prismjs/themes/prism.css';
import moment from 'moment';
import merge from 'lodash.merge';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */
/* global Reflect, Promise */

var extendStatics = function(d, b) {
    extendStatics = Object.setPrototypeOf ||
        ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
        function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
    return extendStatics(d, b);
};

function __extends(d, b) {
    extendStatics(d, b);
    function __() { this.constructor = d; }
    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
}

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : new P(function (resolve) { resolve(result.value); }).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

function __generator(thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
}

/**
 * Child not found error
 */
var ChildNotFoundError = /** @class */ (function () {
    function ChildNotFoundError(name, parentName) {
        this.message = "Child " + name + " not found on parent " + parentName;
    }
    return ChildNotFoundError;
}());

/**
 * Base class for all Zeedhi components.
 */
var Component = /** @class */ (function () {
    /**
     * Creates a new Component.
     * @param props Components properties
     */
    function Component(props) {
        /**
         * Children component structure if the component has children.
         */
        this.children = [];
        /**
         * All children instances if the component has children
         */
        this.childrenInstances = [];
        /**
         * CSS classes to be added to the DOM element when rendering.
         * To apply multiple CSS classes use space as separator
         * (e.g. <samp>\"class1 class2\"</samp>).
         */
        this.cssClass = '';
        /**
         * Events registered to the component.
         */
        this.events = {};
        /**
         * Controls component visibility.
         */
        this.isVisible = true;
        /**
         * Properties accessors to controller.
         */
        this.accessors = {};
        /**
         * Component properties that must have accessors.
         */
        this.props = {};
        this.parent = props.parent;
        this.name = props.name;
        this.events = Event.factory(props.events || this.events);
        this.cssClass = this.getInitValue('cssClass', props.cssClass, this.cssClass);
        this.isVisible = this.getInitValue('isVisible', props.isVisible, this.isVisible);
        this.children = Array.isArray(props.children) ? props.children : this.children;
        this.createAccessors();
    }
    /**
     * Retrieves initial value for the attribute and if it is an accessor,
     * updates props value.
     * @param name Attribute name
     * @param value Attribute value
     * @param defaultValue Attribute default value
     * @returns Initial value
     */
    Component.prototype.getInitValue = function (name, value, defaultValue) {
        var returnValue = defaultValue;
        if (typeof value !== 'undefined') {
            returnValue = value;
            if (Accessor.isAccessorDefinition(value)) {
                this.props[name] = value;
            }
        }
        return returnValue;
    };
    /**
     * Creates all accessors based on props value.
     */
    Component.prototype.createAccessors = function () {
        var _this = this;
        Object.keys(this.props).forEach(function (name) { return _this.createAccessor(name); });
        this.props = {};
    };
    /**
     * Creates a new accessor.
     * @param name Accessor name
     */
    Component.prototype.createAccessor = function (name) {
        var _a = Accessor.getAccessor(this.props[name]), controller = _a[0], accessor = _a[1];
        this.accessors[name] = { accessor: accessor, instance: Loader.getInstance(controller) };
        this.defineAccessor(name);
    };
    /**
     * Replaces instance property to accessor reference.
     * @param name Accessor name
     */
    Component.prototype.defineAccessor = function (name) {
        var _this = this;
        Object.defineProperty(this, name, {
            get: function () { return _this.accessors[name].instance[_this.accessors[name].accessor]; },
            set: function (value) {
                _this.accessors[name].instance[_this.accessors[name].accessor] = value;
            },
            enumerable: true,
            configurable: true,
        });
    };
    /**
     * Adds a new child
     * @param child Child component structure
     */
    Component.prototype.addChild = function (child) {
        this.children.push(child);
    };
    /**
     * Removes child by name
     * @param name Child name
     */
    Component.prototype.removeChild = function (name) {
        var index = this.children.findIndex(function (child) { return child.name === name; });
        if (index !== -1) {
            this.children.splice(index, 1);
            this.childrenInstances.splice(index, 1);
        }
    };
    /**
     * Retrieves a child instance by name
     * @param name Child name
     * @throws Will throw if child not exists
     */
    Component.prototype.getChildInstance = function (name) {
        var child = this.childrenInstances.find(function (child) { return child.name === name; });
        if (child) {
            return child;
        }
        throw new ChildNotFoundError(name, this.name);
    };
    /**
     * Adds new child instance
     * @param instance Child instance
     */
    Component.prototype.addChildInstance = function (instance) {
        this.childrenInstances.push(instance);
    };
    /**
     * Triggered when the component is created.
     */
    Component.prototype.onCreated = function () {
        this.componentId = Metadata.addInstance(this);
        if (this.parent) {
            this.parent.addChildInstance(this);
        }
        if (this.events.onCreated) {
            this.events.onCreated({ component: this });
        }
    };
    /**
     * Triggered before component is mounted.
     */
    Component.prototype.onBeforeMount = function () {
        if (this.events.onBeforeMount) {
            this.events.onBeforeMount({ component: this });
        }
    };
    /**
     * Triggered when the component is mounted.
     * @param element Element mounted reference
     */
    Component.prototype.onMounted = function (element) {
        if (this.events.onMounted) {
            this.events.onMounted({ element: element, component: this });
        }
    };
    /**
     * Triggered before the component is destroyed.
     */
    Component.prototype.onBeforeDestroy = function () {
        if (this.events.onBeforeDestroy) {
            this.events.onBeforeDestroy({ component: this });
        }
    };
    /**
     * Triggered when the component is destroyed.
     */
    Component.prototype.onDestroyed = function () {
        Metadata.clearInstance(this.name, this.componentId);
        if (this.events.onDestroyed) {
            this.events.onDestroyed({ component: this });
        }
    };
    /**
     * Triggered when the component is clicked.
     * @param event DOM event
     * @param element Element clicked
     */
    Component.prototype.click = function (event, element) {
        if (this.events.click) {
            this.events.click({ event: event, element: element, component: this });
        }
    };
    /**
     * Triggered when the component is focused.
     * @param event DOM event
     * @param element Element focused
     */
    Component.prototype.focus = function (event, element) {
        if (this.events.focus) {
            this.events.focus({ event: event, element: element, component: this });
        }
    };
    /**
     * Triggered when the component loses focus.
     * @param event DOM event
     * @param element Element that lost the focus
     */
    Component.prototype.blur = function (event, element) {
        if (this.events.blur) {
            this.events.blur({ event: event, element: element, component: this });
        }
    };
    return Component;
}());

/**
 * Base class for Alert component.
 */
var Alert = /** @class */ (function (_super) {
    __extends(Alert, _super);
    /**
     * Creates a new Alert
     * @param props Alert structure
     */
    function Alert(props) {
        var _this = _super.call(this, props) || this;
        /**
         * Align the component towards the bottom
         */
        _this.bottom = true;
        /**
         * Slot to display flat buttons
         */
        _this.buttonsSlot = [];
        /**
         * Applies specified color to the control.
         * It can be the name of material color or an hexadecimal color
         */
        _this.color = '';
        /**
         * Applies specified color to the dismiss button.
         * It can be the name of material color or an hexadecimal color
         */
        _this.dismissColor = '';
        /**
         * Align the component towards the left
         */
        _this.left = true;
        /**
         * Makes the alert higher (mobile)
         */
        _this.multiLine = false;
        /**
         * Align the component towards the right
         */
        _this.right = false;
        /**
         * Controls dismiss button visibility
         */
        _this.showDismiss = false;
        /**
         * Time (in milliseconds) to wait until alert is automatically hidden.
         * Use 0 to keep open indefinitely.
         */
        _this.timeout = 5000;
        /**
         * Text displayed on alert
         */
        _this.text = '';
        /**
         * Align the content towards the top
         */
        _this.top = false;
        /**
         * Alert type (normal, success, error, warning, info)
         */
        _this.type = 'normal';
        /**
         * Stacks alert content vertically (mobile)
         */
        _this.vertical = false;
        _this.defaultValues = {
            name: _this.name,
            color: _this.color,
            text: _this.text,
            timeout: _this.timeout,
            type: _this.type,
            top: _this.top,
            right: _this.right,
            bottom: _this.bottom,
            left: _this.left,
            vertical: _this.vertical,
            multiLine: _this.multiLine,
            showDismiss: _this.showDismiss,
            dismissColor: _this.dismissColor,
            buttonsSlot: _this.buttonsSlot,
        };
        _this.assignAlertProperties(props);
        _this.createAccessors();
        return _this;
    }
    /**
     * Assign all alert properties
     * @param alert Alert structure
     */
    Alert.prototype.assignAlertProperties = function (alert) {
        this.name = this.getInitValue('name', alert.name, this.defaultValues.name);
        this.color = this.getInitValue('color', alert.color, this.defaultValues.color);
        this.text = this.getInitValue('text', alert.text, this.defaultValues.text);
        this.timeout = this.getInitValue('timeout', alert.timeout, this.defaultValues.timeout);
        this.type = this.getInitValue('type', alert.type, this.defaultValues.type);
        this.top = this.getInitValue('top', alert.top, this.defaultValues.top);
        this.right = this.getInitValue('right', alert.right, this.defaultValues.right);
        this.bottom = this.getInitValue('bottom', alert.bottom, this.defaultValues.bottom);
        this.left = this.getInitValue('left', alert.left, this.defaultValues.left);
        this.vertical = this.getInitValue('vertical', alert.vertical, this.defaultValues.vertical);
        this.multiLine = this.getInitValue('multiLine', alert.multiLine, this.defaultValues.multiLine);
        this.showDismiss = this.getInitValue('showDismiss', alert.showDismiss, this.defaultValues.showDismiss);
        this.dismissColor = this.getInitValue('dismissColor', alert.dismissColor, this.defaultValues.dismissColor);
        this.buttonsSlot = alert.buttonsSlot || this.defaultValues.buttonsSlot;
        this.isVisible = false;
    };
    /**
     * Displays alert
     */
    Alert.prototype.show = function () {
        this.isVisible = true;
    };
    /**
     * Hides alert
     */
    Alert.prototype.hide = function () {
        this.isVisible = false;
    };
    return Alert;
}(Component));

/**
 * Base class for components that will be rendered
 */
var ComponentRender = /** @class */ (function (_super) {
    __extends(ComponentRender, _super);
    function ComponentRender(props) {
        var _this = _super.call(this, props) || this;
        _this.component = props.component;
        return _this;
    }
    return ComponentRender;
}(Component));

/**
 * Base class for ApexChart component.
 */
var ApexChart = /** @class */ (function (_super) {
    __extends(ApexChart, _super);
    /* istanbul ignore next */
    /**
     * Creates a new ApexChart.
     * @param props ApexChart properties
     */
    function ApexChart(props) {
        var _this = _super.call(this, props) || this;
        /**
         * Defines the chart type. Can be: <samp>line, area, bar, pie, donut,
         * scatter, bubble, heatmap, radialBar, candlestick</samp>
         */
        _this.chartType = 'line';
        /**
         * Defines the chart height. Possible values for width can be
         * <samp>'auto', '100%', '400px' or 400</samp>
         */
        _this.height = 'auto';
        /**
         * Chart options. See <a href=\"https://apexcharts.com/docs/options/\"
         * target=\"_blank\" alt=\"Apex Charts API Reference\">Apex Chart API
         * Reference</a> for more information.
         */
        _this.options = {};
        /**
         * Chart series data. See <a href=\"https://apexcharts.com/docs/series/"
         * target=\"_blank\" alt=\"Apex Charts API Reference\">Apex Chart API
         * Reference</a> for more information.
         */
        _this.series = [];
        /**
         * Defines the chart width. Possible values for width can be
         * <samp>'auto', '100%', '400px' or 400</samp>
         */
        _this.width = '100%';
        _this.chartType = _this.getInitValue('chartType', props.chartType, _this.chartType);
        _this.options = _this.getInitValue('options', props.options, _this.options);
        _this.series = _this.getInitValue('series', props.series, _this.series);
        _this.height = _this.getInitValue('height', props.height, _this.height);
        _this.width = _this.getInitValue('width', props.width, _this.width);
        _this.createAccessors();
        _this.createOptionsAccessors(_this.options);
        return _this;
    }
    ApexChart.prototype.createOptionsAccessors = function (options) {
        var _this = this;
        Object.keys(options).forEach(function (key) {
            if (options[key] instanceof Object) {
                _this.createOptionsAccessors(options[key]);
            }
            else if (Accessor.isAccessorDefinition(options[key])) {
                var _a = Accessor.getAccessor(options[key]), controllerName = _a[0], property = _a[1];
                _this.setAccessor(options, key, controllerName, property);
            }
        });
    };
    ApexChart.prototype.setAccessor = function (options, key, controllerName, property) {
        var instance = Loader.getInstance(controllerName);
        if (instance[property]) {
            if (instance[property] instanceof Function) {
                options[key] = instance[property].bind(instance);
            }
            else {
                Object.defineProperty(options, key, {
                    get: function () { return instance[property]; },
                    set: function (value) {
                        instance[property] = value;
                    },
                    enumerable: true,
                    configurable: true,
                });
            }
        }
        else {
            throw new InvalidAccessorDefinitionError();
        }
    };
    return ApexChart;
}(ComponentRender));

/**
 * Base class for Breadcrumbs component.
 */
var Breadcrumbs = /** @class */ (function (_super) {
    __extends(Breadcrumbs, _super);
    /* istanbul ignore next */
    /**
     * Create a new Breadcrumbs
     * @param props Breadcrumbs definition
     */
    function Breadcrumbs(props) {
        var _this = _super.call(this, props) || this;
        /**
         * Applies the dark theme variant to the component.
         */
        _this.dark = false;
        /**
         * Specifies the dividing string between items.
         */
        _this.divider = '/';
        /**
         * Array of objects for each breadcrumb.
         */
        _this.items = [];
        /**
         * Defines the divider icon name.
         */
        _this.iconName = '';
        /**
         * Increase the font-size of the breadcrumb item text to 16px.
         */
        _this.large = false;
        /**
         * Applies the light theme variant to the component.
         */
        _this.light = true;
        /**
         * Decrease the font-size of the breadcrumb item text to 12px.
         */
        _this.small = false;
        _this.dark = _this.getInitValue('dark', props.dark, _this.dark);
        _this.divider = _this.getInitValue('divider', props.divider, _this.divider);
        _this.items = _this.getInitValue('items', props.items, _this.items);
        _this.large = _this.getInitValue('large', props.large, _this.large);
        _this.light = _this.getInitValue('light', props.light, _this.light);
        _this.iconName = _this.getInitValue('iconName', props.iconName, _this.iconName);
        _this.small = _this.getInitValue('small', props.small, _this.small);
        _this.createAccessors();
        return _this;
    }
    return Breadcrumbs;
}(ComponentRender));

/**
 * Base class for Button component.
 */
var Button = /** @class */ (function (_super) {
    __extends(Button, _super);
    /* istanbul ignore next */
    /**
     * Create a new Button
     * @param props Button definition
     */
    function Button(props) {
        var _this = _super.call(this, props) || this;
        /**
         * Applies position absolute to the button
         */
        _this.absolute = false;
        /**
         * Expands the button to 100% of available space
         */
        _this.block = false;
        /**
         * Aligns the button towards the bottom.
         * This should be used with the absolute or fixed props
         */
        _this.bottom = false;
        /**
         * Applies specified color to the control.
         * It can be the name of material or css color in hexa
         */
        _this.color = 'primary';
        /**
         * Applies the dark theme variant to the button
         */
        _this.dark = false;
        /**
         * Removes the ability to click or target the button
         */
        _this.disabled = false;
        /**
         * Designates the button as a floating-action-button
         */
        _this.fab = false;
        /**
         * Applies position fixed to the button
         */
        _this.fixed = false;
        /**
         * Makes the background transparent (equal to text prop in Vuetify)
         */
        _this.flat = false;
        /**
         * Designates the component as anchor and applies the href attribute
         */
        _this.href = '';
        /**
         * Designates the button as icon, round and flat
         */
        _this.icon = false;
        /**
         * Degines the button icon, that will be rendered before the label
         */
        _this.iconName = '';
        /**
         * Defines the button label
         */
        _this.label = '';
        /**
         * Makes the button large
         */
        _this.large = false;
        /**
         * Aligns the button towards the left.
         * This should be used with the absolute or fixed props
         */
        _this.left = false;
        /**
         * Applies the light theme variant to the button
         */
        _this.light = false;
        /**
         * Makes the background transparent and applies a thin border
         */
        _this.outline = false;
        /**
         * Change the icon to the right.
         */
        _this.reverse = false;
        /**
         * Aligns the button towards the right.
         * This should be used with the absolute or fixed props
         */
        _this.right = false;
        /**
         * Applies a large border radius on the button
         */
        _this.round = false;
        /**
         * Makes the button small
         */
        _this.small = false;
        /**
         * Designates the target attribute when the button has href or to prop
         */
        _this.target = '_self';
        /**
         * Removes the component's border-radius
         */
        _this.tile = false;
        /**
         * Denotes the target route of the link
         */
        _this.to = '';
        /**
         * Aligns the content towards the top.
         * This should be used with the absolute or fixed props
         */
        _this.top = false;
        /**
         * Sets button type
         */
        _this.type = 'button';
        /**
         * Sets the width for the card.
         * Possible values for width can be 'auto', '100%', '400px' or 400
         */
        _this.width = '';
        _this.absolute = _this.getInitValue('absolute', props.absolute, _this.absolute);
        _this.block = _this.getInitValue('block', props.block, _this.block);
        _this.bottom = _this.getInitValue('bottom', props.bottom, _this.bottom);
        _this.color = _this.getInitValue('color', props.color, _this.color);
        _this.dark = _this.getInitValue('dark', props.dark, _this.dark);
        _this.disabled = _this.getInitValue('disabled', props.disabled, _this.disabled);
        _this.fab = _this.getInitValue('fab', props.fab, _this.fab);
        _this.fixed = _this.getInitValue('fixed', props.fixed, _this.fixed);
        _this.flat = _this.getInitValue('flat', props.flat, _this.flat);
        _this.href = _this.getInitValue('href', props.href, _this.href);
        _this.icon = _this.getInitValue('icon', props.icon, _this.icon);
        _this.iconName = _this.getInitValue('iconName', props.iconName, _this.iconName);
        _this.label = _this.getInitValue('label', props.label, _this.label);
        _this.large = _this.getInitValue('large', props.large, _this.large);
        _this.left = _this.getInitValue('left', props.left, _this.left);
        _this.light = _this.getInitValue('light', props.light, _this.light);
        _this.outline = _this.getInitValue('outline', props.outline, _this.outline);
        _this.right = _this.getInitValue('right', props.right, _this.right);
        _this.reverse = _this.getInitValue('reverse', props.reverse, _this.reverse);
        _this.round = _this.getInitValue('round', props.round, _this.round);
        _this.small = _this.getInitValue('small', props.small, _this.small);
        _this.target = _this.getInitValue('target', props.target, _this.target);
        _this.tile = _this.getInitValue('tile', props.tile, _this.tile);
        _this.to = _this.getInitValue('to', props.to, _this.to);
        _this.tooltip = props.tooltip || _this.tooltip;
        _this.top = _this.getInitValue('top', props.top, _this.top);
        _this.type = _this.getInitValue('type', props.type, _this.type);
        _this.width = _this.getInitValue('width', props.width, _this.width);
        _this.createAccessors();
        return _this;
    }
    return Button;
}(ComponentRender));

/**
 * Base class for Card component.
 */
var Card = /** @class */ (function (_super) {
    __extends(Card, _super);
    /* istanbul ignore next */
    /**
     * Creates a new Card.
     * @param props Card properties
     */
    function Card(props) {
        var _this = _super.call(this, props) || this;
        /**
         * Configure the active CSS class applied when the link is active
         */
        _this.activeClass = '';
        /**
         * Setting append prop always appends the relative path to the current path
         */
        _this.append = false;
        /**
         * Applies specified color to the control. It can be the name of material color
         */
        _this.color = '';
        /**
         * Applies the dark theme variant to the component
         */
        _this.dark = false;
        /**
         * Removes the ability to click or target the component
         */
        _this.disabled = false;
        /**
         * Removes the card's elevation
         */
        _this.flat = false;
        /**
         * Will apply an elevation of 4dp when hovered
         */
        _this.hover = false;
        /**
         * Specifies an image background for the card
         */
        _this.img = '';
        /**
         * Applies the light theme variant to the component
         */
        _this.light = false;
        /**
         * Designates that the card is a link
         */
        _this.link = false;
        /**
         * Removes card elevation shadow and adds a thin border
         */
        _this.outlined = false;
        /**
         * Specifies a higher default elevation (8dp)
         */
        _this.raised = false;
        /**
         * Apply the ripple effect to show action from a user
         */
        _this.ripple = false;
        /**
         * Applies a large border radius on the top left and bottom right of the card
         */
        _this.shaped = false;
        /**
         * Removes the component's border-radius
         */
        _this.tile = false;
        _this.activeClass = _this.getInitValue('activeClass', props.activeClass, _this.activeClass);
        _this.append = _this.getInitValue('append', props.append, _this.append);
        _this.color = _this.getInitValue('color', props.color, _this.color);
        _this.dark = _this.getInitValue('dark', props.dark, _this.dark);
        _this.disabled = _this.getInitValue('disabled', props.disabled, _this.disabled);
        _this.elevation = _this.getInitValue('elevation', props.elevation, _this.elevation);
        _this.flat = _this.getInitValue('flat', props.flat, _this.flat);
        _this.height = _this.getInitValue('height', props.height, _this.height);
        _this.hover = _this.getInitValue('hover', props.hover, _this.hover);
        _this.href = _this.getInitValue('href', props.href, _this.href);
        _this.img = _this.getInitValue('img', props.img, _this.img);
        _this.light = _this.getInitValue('light', props.light, _this.light);
        _this.link = _this.getInitValue('link', props.link, _this.link);
        _this.maxHeight = _this.getInitValue('maxHeight', props.maxHeight, _this.maxHeight);
        _this.maxWidth = _this.getInitValue('maxWidth', props.maxWidth, _this.maxWidth);
        _this.minHeight = _this.getInitValue('minHeight', props.minHeight, _this.minHeight);
        _this.minWidth = _this.getInitValue('minWidth', props.minWidth, _this.minWidth);
        _this.outlined = _this.getInitValue('outlined', props.outlined, _this.outlined);
        _this.raised = _this.getInitValue('raised', props.raised, _this.raised);
        _this.ripple = _this.getInitValue('ripple', props.ripple, _this.ripple);
        _this.shaped = _this.getInitValue('shaped', props.shaped, _this.shaped);
        _this.tooltip = props.tooltip || _this.tooltip;
        _this.width = _this.getInitValue('width', props.width, _this.width);
        return _this;
    }
    return Card;
}(ComponentRender));

Prism.languages.typescript=Prism.languages.extend("javascript",{keyword:/\b(?:abstract|as|async|await|break|case|catch|class|const|constructor|continue|debugger|declare|default|delete|do|else|enum|export|extends|finally|for|from|function|get|if|implements|import|in|instanceof|interface|is|keyof|let|module|namespace|new|null|of|package|private|protected|public|readonly|return|require|set|static|super|switch|this|throw|try|type|typeof|var|void|while|with|yield)\b/,builtin:/\b(?:string|Function|any|number|boolean|Array|symbol|console|Promise|unknown|never)\b/}),Prism.languages.ts=Prism.languages.typescript;

Prism.languages.json={property:{pattern:/"(?:\\.|[^\\"\r\n])*"(?=\s*:)/,greedy:!0},string:{pattern:/"(?:\\.|[^\\"\r\n])*"(?!\s*:)/,greedy:!0},comment:/\/\/.*|\/\*[\s\S]*?(?:\*\/|$)/,number:/-?\d+\.?\d*(e[+-]?\d+)?/i,punctuation:/[{}[\],]/,operator:/:/,boolean:/\b(?:true|false)\b/,null:{pattern:/\bnull\b/,alias:"keyword"}};

Prism.languages.jsonp=Prism.languages.extend("json",{punctuation:/[{}[\]();,.]/}),Prism.languages.insertBefore("jsonp","punctuation",{function:/[_$a-zA-Z\xA0-\uFFFF][$\w\xA0-\uFFFF]*(?=\s*\()/});

!function(e){var t="\\b(?:BASH|BASHOPTS|BASH_ALIASES|BASH_ARGC|BASH_ARGV|BASH_CMDS|BASH_COMPLETION_COMPAT_DIR|BASH_LINENO|BASH_REMATCH|BASH_SOURCE|BASH_VERSINFO|BASH_VERSION|COLORTERM|COLUMNS|COMP_WORDBREAKS|DBUS_SESSION_BUS_ADDRESS|DEFAULTS_PATH|DESKTOP_SESSION|DIRSTACK|DISPLAY|EUID|GDMSESSION|GDM_LANG|GNOME_KEYRING_CONTROL|GNOME_KEYRING_PID|GPG_AGENT_INFO|GROUPS|HISTCONTROL|HISTFILE|HISTFILESIZE|HISTSIZE|HOME|HOSTNAME|HOSTTYPE|IFS|INSTANCE|JOB|LANG|LANGUAGE|LC_ADDRESS|LC_ALL|LC_IDENTIFICATION|LC_MEASUREMENT|LC_MONETARY|LC_NAME|LC_NUMERIC|LC_PAPER|LC_TELEPHONE|LC_TIME|LESSCLOSE|LESSOPEN|LINES|LOGNAME|LS_COLORS|MACHTYPE|MAILCHECK|MANDATORY_PATH|NO_AT_BRIDGE|OLDPWD|OPTERR|OPTIND|ORBIT_SOCKETDIR|OSTYPE|PAPERSIZE|PATH|PIPESTATUS|PPID|PS1|PS2|PS3|PS4|PWD|RANDOM|REPLY|SECONDS|SELINUX_INIT|SESSION|SESSIONTYPE|SESSION_MANAGER|SHELL|SHELLOPTS|SHLVL|SSH_AUTH_SOCK|TERM|UID|UPSTART_EVENTS|UPSTART_INSTANCE|UPSTART_JOB|UPSTART_SESSION|USER|WINDOWID|XAUTHORITY|XDG_CONFIG_DIRS|XDG_CURRENT_DESKTOP|XDG_DATA_DIRS|XDG_GREETER_DATA_DIR|XDG_MENU_PREFIX|XDG_RUNTIME_DIR|XDG_SEAT|XDG_SEAT_PATH|XDG_SESSION_DESKTOP|XDG_SESSION_ID|XDG_SESSION_PATH|XDG_SESSION_TYPE|XDG_VTNR|XMODIFIERS)\\b",n={environment:{pattern:RegExp("\\$"+t),alias:"constant"},variable:[{pattern:/\$?\(\([\s\S]+?\)\)/,greedy:!0,inside:{variable:[{pattern:/(^\$\(\([\s\S]+)\)\)/,lookbehind:!0},/^\$\(\(/],number:/\b0x[\dA-Fa-f]+\b|(?:\b\d+\.?\d*|\B\.\d+)(?:[Ee]-?\d+)?/,operator:/--?|-=|\+\+?|\+=|!=?|~|\*\*?|\*=|\/=?|%=?|<<=?|>>=?|<=?|>=?|==?|&&?|&=|\^=?|\|\|?|\|=|\?|:/,punctuation:/\(\(?|\)\)?|,|;/}},{pattern:/\$\((?:\([^)]+\)|[^()])+\)|`[^`]+`/,greedy:!0,inside:{variable:/^\$\(|^`|\)$|`$/}},{pattern:/\$\{[^}]+\}/,greedy:!0,inside:{operator:/:[-=?+]?|[!\/]|##?|%%?|\^\^?|,,?/,punctuation:/[\[\]]/,environment:{pattern:RegExp("(\\{)"+t),lookbehind:!0,alias:"constant"}}},/\$(?:\w+|[#?*!@$])/],entity:/\\(?:[abceEfnrtv\\"]|O?[0-7]{1,3}|x[0-9a-fA-F]{1,2}|u[0-9a-fA-F]{4}|U[0-9a-fA-F]{8})/};e.languages.bash={shebang:{pattern:/^#!\s*\/.*/,alias:"important"},comment:{pattern:/(^|[^"{\\$])#.*/,lookbehind:!0},"function-name":[{pattern:/(\bfunction\s+)\w+(?=(?:\s*\(?:\s*\))?\s*\{)/,lookbehind:!0,alias:"function"},{pattern:/\b\w+(?=\s*\(\s*\)\s*\{)/,alias:"function"}],"for-or-select":{pattern:/(\b(?:for|select)\s+)\w+(?=\s+in\s)/,alias:"variable",lookbehind:!0},"assign-left":{pattern:/(^|[\s;|&]|[<>]\()\w+(?=\+?=)/,inside:{environment:{pattern:RegExp("(^|[\\s;|&]|[<>]\\()"+t),lookbehind:!0,alias:"constant"}},alias:"variable",lookbehind:!0},string:[{pattern:/((?:^|[^<])<<-?\s*)(\w+?)\s*(?:\r?\n|\r)(?:[\s\S])*?(?:\r?\n|\r)\2/,lookbehind:!0,greedy:!0,inside:n},{pattern:/((?:^|[^<])<<-?\s*)(["'])(\w+)\2\s*(?:\r?\n|\r)(?:[\s\S])*?(?:\r?\n|\r)\3/,lookbehind:!0,greedy:!0},{pattern:/(["'])(?:\\[\s\S]|\$\([^)]+\)|`[^`]+`|(?!\1)[^\\])*\1/,greedy:!0,inside:n}],environment:{pattern:RegExp("\\$?"+t),alias:"constant"},variable:n.variable,function:{pattern:/(^|[\s;|&]|[<>]\()(?:add|apropos|apt|aptitude|apt-cache|apt-get|aspell|automysqlbackup|awk|basename|bash|bc|bconsole|bg|bzip2|cal|cat|cfdisk|chgrp|chkconfig|chmod|chown|chroot|cksum|clear|cmp|column|comm|cp|cron|crontab|csplit|curl|cut|date|dc|dd|ddrescue|debootstrap|df|diff|diff3|dig|dir|dircolors|dirname|dirs|dmesg|du|egrep|eject|env|ethtool|expand|expect|expr|fdformat|fdisk|fg|fgrep|file|find|fmt|fold|format|free|fsck|ftp|fuser|gawk|git|gparted|grep|groupadd|groupdel|groupmod|groups|grub-mkconfig|gzip|halt|head|hg|history|host|hostname|htop|iconv|id|ifconfig|ifdown|ifup|import|install|ip|jobs|join|kill|killall|less|link|ln|locate|logname|logrotate|look|lpc|lpr|lprint|lprintd|lprintq|lprm|ls|lsof|lynx|make|man|mc|mdadm|mkconfig|mkdir|mke2fs|mkfifo|mkfs|mkisofs|mknod|mkswap|mmv|more|most|mount|mtools|mtr|mutt|mv|nano|nc|netstat|nice|nl|nohup|notify-send|npm|nslookup|op|open|parted|passwd|paste|pathchk|ping|pkill|pnpm|popd|pr|printcap|printenv|ps|pushd|pv|quota|quotacheck|quotactl|ram|rar|rcp|reboot|remsync|rename|renice|rev|rm|rmdir|rpm|rsync|scp|screen|sdiff|sed|sendmail|seq|service|sftp|sh|shellcheck|shuf|shutdown|sleep|slocate|sort|split|ssh|stat|strace|su|sudo|sum|suspend|swapon|sync|tac|tail|tar|tee|time|timeout|top|touch|tr|traceroute|tsort|tty|umount|uname|unexpand|uniq|units|unrar|unshar|unzip|update-grub|uptime|useradd|userdel|usermod|users|uudecode|uuencode|v|vdir|vi|vim|virsh|vmstat|wait|watch|wc|wget|whereis|which|who|whoami|write|xargs|xdg-open|yarn|yes|zenity|zip|zsh|zypper)(?=$|[)\s;|&])/,lookbehind:!0},keyword:{pattern:/(^|[\s;|&]|[<>]\()(?:if|then|else|elif|fi|for|while|in|case|esac|function|select|do|done|until)(?=$|[)\s;|&])/,lookbehind:!0},builtin:{pattern:/(^|[\s;|&]|[<>]\()(?:\.|:|break|cd|continue|eval|exec|exit|export|getopts|hash|pwd|readonly|return|shift|test|times|trap|umask|unset|alias|bind|builtin|caller|command|declare|echo|enable|help|let|local|logout|mapfile|printf|read|readarray|source|type|typeset|ulimit|unalias|set|shopt)(?=$|[)\s;|&])/,lookbehind:!0,alias:"class-name"},boolean:{pattern:/(^|[\s;|&]|[<>]\()(?:true|false)(?=$|[)\s;|&])/,lookbehind:!0},"file-descriptor":{pattern:/\B&\d\b/,alias:"important"},operator:{pattern:/\d?<>|>\||\+=|==?|!=?|=~|<<[<-]?|[&\d]?>>|\d?[<>]&?|&[>&]?|\|[&|]?|<=?|>=?/,inside:{"file-descriptor":{pattern:/^\d/,alias:"important"}}},punctuation:/\$?\(\(?|\)\)?|\.\.|[{}[\];\\]/,number:{pattern:/(^|\s)(?:[1-9]\d*|0)(?:[.,]\d+)?\b/,lookbehind:!0}};for(var a=["comment","function-name","for-or-select","assign-left","string","environment","function","keyword","builtin","boolean","file-descriptor","operator","punctuation","number"],r=n.variable[1].inside,s=0;s<a.length;s++)r[a[s]]=e.languages.bash[a[s]];e.languages.shell=e.languages.bash;}(Prism);

!function(e){var t=/\b(?:abstract|continue|for|new|switch|assert|default|goto|package|synchronized|boolean|do|if|private|this|break|double|implements|protected|throw|byte|else|import|public|throws|case|enum|instanceof|return|transient|catch|extends|int|short|try|char|final|interface|static|void|class|finally|long|strictfp|volatile|const|float|native|super|while|var|null|exports|module|open|opens|provides|requires|to|transitive|uses|with)\b/,a=/\b[A-Z](?:\w*[a-z]\w*)?\b/;e.languages.java=e.languages.extend("clike",{"class-name":[a,/\b[A-Z]\w*(?=\s+\w+\s*[;,=())])/],keyword:t,function:[e.languages.clike.function,{pattern:/(\:\:)[a-z_]\w*/,lookbehind:!0}],number:/\b0b[01][01_]*L?\b|\b0x[\da-f_]*\.?[\da-f_p+-]+\b|(?:\b\d[\d_]*\.?[\d_]*|\B\.\d[\d_]*)(?:e[+-]?\d[\d_]*)?[dfl]?/i,operator:{pattern:/(^|[^.])(?:<<=?|>>>?=?|->|([-+&|])\2|[?:~]|[-+*/%&|^!=<>]=?)/m,lookbehind:!0}}),e.languages.insertBefore("java","class-name",{annotation:{alias:"punctuation",pattern:/(^|[^.])@\w+/,lookbehind:!0},namespace:{pattern:/(\b(?:exports|import(?:\s+static)?|module|open|opens|package|provides|requires|to|transitive|uses|with)\s+)[a-z]\w*(\.[a-z]\w*)+/,lookbehind:!0,inside:{punctuation:/\./}},generics:{pattern:/<(?:[\w\s,.&?]|<(?:[\w\s,.&?]|<(?:[\w\s,.&?]|<[\w\s,.&?]*>)*>)*>)*>/,inside:{"class-name":a,keyword:t,punctuation:/[<>(),.:]/,operator:/[?&|]/}}});}(Prism);

!function(e){e.languages.kotlin=e.languages.extend("clike",{keyword:{pattern:/(^|[^.])\b(?:abstract|actual|annotation|as|break|by|catch|class|companion|const|constructor|continue|crossinline|data|do|dynamic|else|enum|expect|external|final|finally|for|fun|get|if|import|in|infix|init|inline|inner|interface|internal|is|lateinit|noinline|null|object|open|operator|out|override|package|private|protected|public|reified|return|sealed|set|super|suspend|tailrec|this|throw|to|try|typealias|val|var|vararg|when|where|while)\b/,lookbehind:!0},function:[/\w+(?=\s*\()/,{pattern:/(\.)\w+(?=\s*\{)/,lookbehind:!0}],number:/\b(?:0[xX][\da-fA-F]+(?:_[\da-fA-F]+)*|0[bB][01]+(?:_[01]+)*|\d+(?:_\d+)*(?:\.\d+(?:_\d+)*)?(?:[eE][+-]?\d+(?:_\d+)*)?[fFL]?)\b/,operator:/\+[+=]?|-[-=>]?|==?=?|!(?:!|==?)?|[\/*%<>]=?|[?:]:?|\.\.|&&|\|\||\b(?:and|inv|or|shl|shr|ushr|xor)\b/}),delete e.languages.kotlin["class-name"],e.languages.insertBefore("kotlin","string",{"raw-string":{pattern:/("""|''')[\s\S]*?\1/,alias:"string"}}),e.languages.insertBefore("kotlin","keyword",{annotation:{pattern:/\B@(?:\w+:)?(?:[A-Z]\w*|\[[^\]]+\])/,alias:"builtin"}}),e.languages.insertBefore("kotlin","function",{label:{pattern:/\w+@|@\w+/,alias:"symbol"}});var n=[{pattern:/\$\{[^}]+\}/,inside:{delimiter:{pattern:/^\$\{|\}$/,alias:"variable"},rest:e.languages.kotlin}},{pattern:/\$\w+/,alias:"variable"}];e.languages.kotlin.string.inside=e.languages.kotlin["raw-string"].inside={interpolation:n};}(Prism);

Prism.languages.yaml={scalar:{pattern:/([\-:]\s*(?:![^\s]+)?[ \t]*[|>])[ \t]*(?:((?:\r?\n|\r)[ \t]+)[^\r\n]+(?:\2[^\r\n]+)*)/,lookbehind:!0,alias:"string"},comment:/#.*/,key:{pattern:/(\s*(?:^|[:\-,[{\r\n?])[ \t]*(?:![^\s]+)?[ \t]*)[^\r\n{[\]},#\s]+?(?=\s*:\s)/,lookbehind:!0,alias:"atrule"},directive:{pattern:/(^[ \t]*)%.+/m,lookbehind:!0,alias:"important"},datetime:{pattern:/([:\-,[{]\s*(?:![^\s]+)?[ \t]*)(?:\d{4}-\d\d?-\d\d?(?:[tT]|[ \t]+)\d\d?:\d{2}:\d{2}(?:\.\d*)?[ \t]*(?:Z|[-+]\d\d?(?::\d{2})?)?|\d{4}-\d{2}-\d{2}|\d\d?:\d{2}(?::\d{2}(?:\.\d*)?)?)(?=[ \t]*(?:$|,|]|}))/m,lookbehind:!0,alias:"number"},boolean:{pattern:/([:\-,[{]\s*(?:![^\s]+)?[ \t]*)(?:true|false)[ \t]*(?=$|,|]|})/im,lookbehind:!0,alias:"important"},null:{pattern:/([:\-,[{]\s*(?:![^\s]+)?[ \t]*)(?:null|~)[ \t]*(?=$|,|]|})/im,lookbehind:!0,alias:"important"},string:{pattern:/([:\-,[{]\s*(?:![^\s]+)?[ \t]*)("|')(?:(?!\2)[^\\\r\n]|\\.)*\2(?=[ \t]*(?:$|,|]|}|\s*#))/m,lookbehind:!0,greedy:!0},number:{pattern:/([:\-,[{]\s*(?:![^\s]+)?[ \t]*)[+-]?(?:0x[\da-f]+|0o[0-7]+|(?:\d+\.?\d*|\.?\d+)(?:e[+-]?\d+)?|\.inf|\.nan)[ \t]*(?=$|,|]|})/im,lookbehind:!0},tag:/![^\s]+/,important:/[&*][\w]+/,punctuation:/---|[:[\]{}\-,|>?]|\.\.\./},Prism.languages.yml=Prism.languages.yaml;

Prism.languages.docker={keyword:{pattern:/(^\s*)(?:ADD|ARG|CMD|COPY|ENTRYPOINT|ENV|EXPOSE|FROM|HEALTHCHECK|LABEL|MAINTAINER|ONBUILD|RUN|SHELL|STOPSIGNAL|USER|VOLUME|WORKDIR)(?=\s)/im,lookbehind:!0},string:/("|')(?:(?!\1)[^\\\r\n]|\\(?:\r\n|[\s\S]))*\1/,comment:/#.*/,punctuation:/---|\.\.\.|[:[\]{}\-,|>?]/},Prism.languages.dockerfile=Prism.languages.docker;

Prism.languages.sql={comment:{pattern:/(^|[^\\])(?:\/\*[\s\S]*?\*\/|(?:--|\/\/|#).*)/,lookbehind:!0},variable:[{pattern:/@(["'`])(?:\\[\s\S]|(?!\1)[^\\])+\1/,greedy:!0},/@[\w.$]+/],string:{pattern:/(^|[^@\\])("|')(?:\\[\s\S]|(?!\2)[^\\]|\2\2)*\2/,greedy:!0,lookbehind:!0},function:/\b(?:AVG|COUNT|FIRST|FORMAT|LAST|LCASE|LEN|MAX|MID|MIN|MOD|NOW|ROUND|SUM|UCASE)(?=\s*\()/i,keyword:/\b(?:ACTION|ADD|AFTER|ALGORITHM|ALL|ALTER|ANALYZE|ANY|APPLY|AS|ASC|AUTHORIZATION|AUTO_INCREMENT|BACKUP|BDB|BEGIN|BERKELEYDB|BIGINT|BINARY|BIT|BLOB|BOOL|BOOLEAN|BREAK|BROWSE|BTREE|BULK|BY|CALL|CASCADED?|CASE|CHAIN|CHAR(?:ACTER|SET)?|CHECK(?:POINT)?|CLOSE|CLUSTERED|COALESCE|COLLATE|COLUMNS?|COMMENT|COMMIT(?:TED)?|COMPUTE|CONNECT|CONSISTENT|CONSTRAINT|CONTAINS(?:TABLE)?|CONTINUE|CONVERT|CREATE|CROSS|CURRENT(?:_DATE|_TIME|_TIMESTAMP|_USER)?|CURSOR|CYCLE|DATA(?:BASES?)?|DATE(?:TIME)?|DAY|DBCC|DEALLOCATE|DEC|DECIMAL|DECLARE|DEFAULT|DEFINER|DELAYED|DELETE|DELIMITERS?|DENY|DESC|DESCRIBE|DETERMINISTIC|DISABLE|DISCARD|DISK|DISTINCT|DISTINCTROW|DISTRIBUTED|DO|DOUBLE|DROP|DUMMY|DUMP(?:FILE)?|DUPLICATE|ELSE(?:IF)?|ENABLE|ENCLOSED|END|ENGINE|ENUM|ERRLVL|ERRORS|ESCAPED?|EXCEPT|EXEC(?:UTE)?|EXISTS|EXIT|EXPLAIN|EXTENDED|FETCH|FIELDS|FILE|FILLFACTOR|FIRST|FIXED|FLOAT|FOLLOWING|FOR(?: EACH ROW)?|FORCE|FOREIGN|FREETEXT(?:TABLE)?|FROM|FULL|FUNCTION|GEOMETRY(?:COLLECTION)?|GLOBAL|GOTO|GRANT|GROUP|HANDLER|HASH|HAVING|HOLDLOCK|HOUR|IDENTITY(?:_INSERT|COL)?|IF|IGNORE|IMPORT|INDEX|INFILE|INNER|INNODB|INOUT|INSERT|INT|INTEGER|INTERSECT|INTERVAL|INTO|INVOKER|ISOLATION|ITERATE|JOIN|KEYS?|KILL|LANGUAGE|LAST|LEAVE|LEFT|LEVEL|LIMIT|LINENO|LINES|LINESTRING|LOAD|LOCAL|LOCK|LONG(?:BLOB|TEXT)|LOOP|MATCH(?:ED)?|MEDIUM(?:BLOB|INT|TEXT)|MERGE|MIDDLEINT|MINUTE|MODE|MODIFIES|MODIFY|MONTH|MULTI(?:LINESTRING|POINT|POLYGON)|NATIONAL|NATURAL|NCHAR|NEXT|NO|NONCLUSTERED|NULLIF|NUMERIC|OFF?|OFFSETS?|ON|OPEN(?:DATASOURCE|QUERY|ROWSET)?|OPTIMIZE|OPTION(?:ALLY)?|ORDER|OUT(?:ER|FILE)?|OVER|PARTIAL|PARTITION|PERCENT|PIVOT|PLAN|POINT|POLYGON|PRECEDING|PRECISION|PREPARE|PREV|PRIMARY|PRINT|PRIVILEGES|PROC(?:EDURE)?|PUBLIC|PURGE|QUICK|RAISERROR|READS?|REAL|RECONFIGURE|REFERENCES|RELEASE|RENAME|REPEAT(?:ABLE)?|REPLACE|REPLICATION|REQUIRE|RESIGNAL|RESTORE|RESTRICT|RETURNS?|REVOKE|RIGHT|ROLLBACK|ROUTINE|ROW(?:COUNT|GUIDCOL|S)?|RTREE|RULE|SAVE(?:POINT)?|SCHEMA|SECOND|SELECT|SERIAL(?:IZABLE)?|SESSION(?:_USER)?|SET(?:USER)?|SHARE|SHOW|SHUTDOWN|SIMPLE|SMALLINT|SNAPSHOT|SOME|SONAME|SQL|START(?:ING)?|STATISTICS|STATUS|STRIPED|SYSTEM_USER|TABLES?|TABLESPACE|TEMP(?:ORARY|TABLE)?|TERMINATED|TEXT(?:SIZE)?|THEN|TIME(?:STAMP)?|TINY(?:BLOB|INT|TEXT)|TOP?|TRAN(?:SACTIONS?)?|TRIGGER|TRUNCATE|TSEQUAL|TYPES?|UNBOUNDED|UNCOMMITTED|UNDEFINED|UNION|UNIQUE|UNLOCK|UNPIVOT|UNSIGNED|UPDATE(?:TEXT)?|USAGE|USE|USER|USING|VALUES?|VAR(?:BINARY|CHAR|CHARACTER|YING)|VIEW|WAITFOR|WARNINGS|WHEN|WHERE|WHILE|WITH(?: ROLLUP|IN)?|WORK|WRITE(?:TEXT)?|YEAR)\b/i,boolean:/\b(?:TRUE|FALSE|NULL)\b/i,number:/\b0x[\da-f]+\b|\b\d+\.?\d*|\B\.\d+\b/i,operator:/[-+*\/=%^~]|&&?|\|\|?|!=?|<(?:=>?|<|>)?|>[>=]?|\b(?:AND|BETWEEN|IN|LIKE|NOT|OR|IS|DIV|REGEXP|RLIKE|SOUNDS LIKE|XOR)\b/i,punctuation:/[;[\]()`,.]/};

Prism.languages.groovy=Prism.languages.extend("clike",{keyword:/\b(?:as|def|in|abstract|assert|boolean|break|byte|case|catch|char|class|const|continue|default|do|double|else|enum|extends|final|finally|float|for|goto|if|implements|import|instanceof|int|interface|long|native|new|package|private|protected|public|return|short|static|strictfp|super|switch|synchronized|this|throw|throws|trait|transient|try|void|volatile|while)\b/,string:[{pattern:/("""|''')[\s\S]*?\1|(?:\$\/)(?:\$\/\$|[\s\S])*?\/\$/,greedy:!0},{pattern:/(["'\/])(?:\\.|(?!\1)[^\\\r\n])*\1/,greedy:!0}],number:/\b(?:0b[01_]+|0x[\da-f_]+(?:\.[\da-f_p\-]+)?|[\d_]+(?:\.[\d_]+)?(?:e[+-]?[\d]+)?)[glidf]?\b/i,operator:{pattern:/(^|[^.])(?:~|==?~?|\?[.:]?|\*(?:[.=]|\*=?)?|\.[@&]|\.\.<|\.{1,2}(?!\.)|-[-=>]?|\+[+=]?|!=?|<(?:<=?|=>?)?|>(?:>>?=?|=)?|&[&=]?|\|[|=]?|\/=?|\^=?|%=?)/,lookbehind:!0},punctuation:/\.+|[{}[\];(),:$]/}),Prism.languages.insertBefore("groovy","string",{shebang:{pattern:/#!.+/,alias:"comment"}}),Prism.languages.insertBefore("groovy","punctuation",{"spock-block":/\b(?:setup|given|when|then|and|cleanup|expect|where):/}),Prism.languages.insertBefore("groovy","function",{annotation:{alias:"punctuation",pattern:/(^|[^.])@\w+/,lookbehind:!0}}),Prism.hooks.add("wrap",function(e){if("groovy"===e.language&&"string"===e.type){var t=e.content[0];if("'"!=t){var n=/([^\\])(?:\$(?:\{.*?\}|[\w.]+))/;"$"===t&&(n=/([^\$])(?:\$(?:\{.*?\}|[\w.]+))/),e.content=e.content.replace(/&lt;/g,"<").replace(/&amp;/g,"&"),e.content=Prism.highlight(e.content,{expression:{pattern:n,lookbehind:!0,inside:Prism.languages.groovy}}),e.classes.push("/"===t?"regex":"gstring");}}});

/**
 * The Code Editor component <code>zd-code-editor</code> is used to show code highlighted using one of the
 * language suported by <a href=\"http://prismjs.com\" alt=\"Prism JS\" target=\"_blank\">Prism JS</a>
 * (<strong>html</strong>, <strong>javascript</strong>, <strong>typescript</strong>, <strong>css</strong>,
 * <strong>json</strong>, <strong>bash</strong>, <strong>etc.</strong>).
 * It can show code from a datasource field or static code.
 */
var CodeEditor = /** @class */ (function (_super) {
    __extends(CodeEditor, _super);
    /* istanbul ignore next */
    /**
     * Creates a new CodeEditor
     * @param props CodeEditor properties
     */
    function CodeEditor(props) {
        var _this = _super.call(this, props) || this;
        /**
         * Code to be shown. When showing JSON code, the JSON object can be passed instead of the string code.
         */
        _this.staticCode = '';
        /**
         * Max height in pixels. 'none' means no limit.
         */
        _this.maxHeight = 'none';
        /**
         * Language code to be used for highlight (js|javascript, css, html, json, ts|typescript, bash|shell)
         * Other languages must be imported above
         */
        _this.language = 'ts';
        /**
         * Controls if line numbers are shown
         */
        _this.showLineNumbers = true;
        _this.tooltipText = I18n.instance.t('Copy');
        _this.code = '';
        _this.staticCode = props.staticCode || _this.staticCode;
        _this.maxHeight = props.maxHeight || _this.maxHeight;
        _this.language = props.language || _this.language;
        _this.showLineNumbers = props.showLineNumbers !== false;
        _this.code = _this.getCode();
        return _this;
    }
    /**
     * Retrieves static code as string
     */
    CodeEditor.prototype.getCode = function () {
        var result = '';
        if (this.staticCode) {
            result = this.staticCode;
            if (Array.isArray(result)) {
                result = result.map(function (item) { return (typeof item === 'object' ? JSON.stringify(item, null, 2) : item); }).join('\n');
            }
            else if (typeof result === 'object') {
                result = JSON.stringify(result, null, 2);
            }
        }
        return result;
    };
    /**
     * Copy the component content to clipboard
     */
    CodeEditor.prototype.copyToClipboard = function () {
        return __awaiter(this, void 0, void 0, function () {
            var text, span, selection, range, success;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        text = this.code;
                        if (!navigator.clipboard) return [3 /*break*/, 2];
                        return [4 /*yield*/, navigator.clipboard.writeText(text)];
                    case 1:
                        _a.sent();
                        this.changeTooltipText();
                        return [2 /*return*/, Promise.resolve()];
                    case 2:
                        span = document.createElement('span');
                        span.textContent = text;
                        // Preserve consecutive spaces and newlines
                        span.style.whiteSpace = 'pre';
                        span.style.opacity = '0';
                        // Add the <span> to the page
                        document.body.appendChild(span);
                        selection = window.getSelection();
                        range = window.document.createRange();
                        success = false;
                        if (selection) {
                            selection.removeAllRanges();
                            range.selectNode(span);
                            selection.addRange(range);
                            // Copy text to the clipboard
                            success = window.document.execCommand('copy');
                            // Cleanup
                            selection.removeAllRanges();
                            window.document.body.removeChild(span);
                        }
                        if (success) {
                            this.changeTooltipText();
                        }
                        // The Async Clipboard API returns a promise that may reject with `undefined`
                        // so we match that here for consistency.
                        return [2 /*return*/, success
                                ? Promise.resolve()
                                : Promise.reject()];
                }
            });
        });
    };
    CodeEditor.prototype.changeTooltipText = function () {
        var _this = this;
        this.tooltipText = I18n.instance.t('Copied!');
        setTimeout(function () { _this.tooltipText = I18n.instance.t('Copy'); }, 2000);
    };
    /**
     * Get the code highlighted
     */
    CodeEditor.prototype.getHighlightedCode = function () {
        return Prism__default.highlight(this.code, Prism__default.languages[this.language], this.language);
    };
    Object.defineProperty(CodeEditor.prototype, "lineNumbersCount", {
        /**
         * Get the total of lines
         */
        get: function () {
            var totalLines = this.code.split(/\r\n|\n/).length;
            if (this.code.endsWith('\n') || !this.code) {
                totalLines -= 1;
            }
            return totalLines;
        },
        enumerable: true,
        configurable: true
    });
    return CodeEditor;
}(ComponentRender));

/**
 * Base class for Col component.
 */
var Col = /** @class */ (function (_super) {
    __extends(Col, _super);
    /* istanbul ignore next */
    /**
     * Creates a new Col.
     * @param props Col properties
     */
    function Col(props) {
        var _this = _super.call(this, props) || this;
        _this.cols = '12';
        _this.cols = _this.getInitValue('cols', props.cols, _this.cols);
        _this.sm = _this.getInitValue('sm', props.sm, _this.sm);
        _this.md = _this.getInitValue('md', props.md, _this.md);
        _this.lg = _this.getInitValue('lg', props.lg, _this.lg);
        _this.xl = _this.getInitValue('xl', props.xl, _this.xl);
        _this.offset = _this.getInitValue('offset', props.offset, _this.offset);
        _this.createAccessors();
        return _this;
    }
    return Col;
}(ComponentRender));

/**
 * Base class for Container component.
 */
var Container = /** @class */ (function (_super) {
    __extends(Container, _super);
    /* istanbul ignore next */
    /**
     * Creates a new Container.
     * @param props Container properties
     */
    function Container(props) {
        var _this = _super.call(this, props) || this;
        /**
         * Adds height 100% to container.
         */
        _this.fillHeight = false;
        /**
         * Removes viewport maximum-width size breakpoints.
         */
        _this.fluid = true;
        _this.fluid = _this.getInitValue('fluid', props.fluid, _this.fluid);
        _this.fillHeight = _this.getInitValue('fillHeight', props.fillHeight, _this.fillHeight);
        return _this;
    }
    return Container;
}(ComponentRender));

/**
 * Base class for Form component.
 */
var Form = /** @class */ (function (_super) {
    __extends(Form, _super);
    /* istanbul ignore next */
    /**
     * Creates a new Form.
     * @param props Form properties
     */
    function Form(props) {
        var _this = _super.call(this, props) || this;
        /**
         * Children components that are Inputs.
         */
        _this.inputs = {};
        _this.internalValue = {};
        _this.align = _this.getInitValue('align', props.align, _this.align);
        _this.justify = _this.getInitValue('justify', props.justify, _this.justify);
        _this.internalValue = _this.getInitValue('value', props.value, _this.value);
        _this.createAccessors();
        return _this;
    }
    Object.defineProperty(Form.prototype, "value", {
        /**
         * Form value.
         */
        get: function () {
            return this.internalValue;
        },
        set: function (value) {
            for (var key in value) {
                if (this.inputs[key]) {
                    this.inputs[key].value = value[key];
                }
                else {
                    this.internalValue[key] = value[key];
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Saves input instance and defines property on form value.
     * @param component Children component
     */
    Form.prototype.registerInput = function (component) {
        this.inputs[component.name] = component;
        component.value = this.value[component.name] || component.value;
        Object.defineProperty(this.value, component.name, {
            get: function () { return component.value; },
            set: function (value) { component.value = value; },
            enumerable: true,
            configurable: true,
        });
    };
    Object.defineProperty(Form.prototype, "childrenProps", {
        /**
         * Children props without grid system.
         */
        get: function () {
            return this.children.map(function (_a) {
                var grid = _a.grid, otherAttrs = __rest(_a, ["grid"]);
                return otherAttrs;
            });
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Form.prototype, "childrenGrid", {
        /**
         * Grid system for each child.
         */
        get: function () {
            var childrenGrid = {};
            this.children.forEach(function (child) {
                childrenGrid[child.name] = __assign({ cols: 'auto' }, (child.grid || {}));
            });
            return childrenGrid;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Validates form.
     */
    Form.prototype.validate = function () {
        if (this.viewValidate) {
            return this.viewValidate();
        }
        return Object.values(this.inputs).every(function (input) { return input.isValid(); });
    };
    /**
     * Sets view validation method.
     */
    Form.prototype.setViewValidate = function (viewValidate) {
        this.viewValidate = viewValidate;
    };
    /**
     * Triggered when form is submitted and it's valid.
     * @param event DOM Event
     * @param element Form component
     */
    Form.prototype.submit = function (event, element) {
        if (this.events.submit) {
            this.events.submit({ event: event, element: element, component: this });
        }
    };
    return Form;
}(ComponentRender));

/**
 * Base class for Input component.
 */
var Input = /** @class */ (function (_super) {
    __extends(Input, _super);
    /* istanbul ignore next */
    /**
     * Creates a new Input.
     * @param props Input properties
     */
    function Input(props) {
        var _this = _super.call(this, props) || this;
        /**
         * Input content alignment.
         */
        _this.align = 'left';
        /**
         * Add input clear functionality if the input isn't readonly or disabled.
         */
        _this.clearable = true;
        /**
         * Applies the dark theme variant to the input.
         */
        _this.dark = false;
        /**
         * Defines smaller input.
         */
        _this.dense = false;
        /**
         * Removes the ability to click or target the input.
         */
        _this.disabled = false;
        /**
         * Hint text.
         */
        _this.hint = '';
        /**
         * Defines the input label.
         */
        _this.label = '';
        /**
         * Applies the light theme variant to the input.
         */
        _this.light = false;
        /**
         * Applies a custom character mask to the input.
         */
        _this.mask = '';
        /**
         * Forces hint to always be visible.
         */
        _this.persistentHint = false;
        /**
         * Sets the input’s placeholder text.
         */
        _this.placeholder = '';
        /**
         * Puts input in readonly state.
         */
        _this.readonly = false;
        /**
         * Reverses the input orientation.
         */
        _this.reverse = false;
        /**
         * Rules that will validate the input value.
         */
        _this.rules = [];
        /**
         * Shows input border.
         */
        _this.showBorder = true;
        /**
         * Shows input helper.
         */
        _this.showHelper = true;
        /**
         * Shows input label.
         */
        _this.showLabel = true;
        /**
         * Used to watch a value from store.
         */
        _this.storePath = '';
        /**
         * Input value.
         */
        _this.value = undefined;
        /**
         * Input validations.
         */
        _this.validations = {};
        /**
         * Parsed field rules.
         */
        _this.parsedValidations = {};
        _this.formatterFn = FormatterParserProvider.getFormatter('ZdInput');
        _this.parserFn = FormatterParserProvider.getParser('ZdInput');
        _this.align = _this.getInitValue('align', props.align, _this.align);
        _this.clearable = _this.getInitValue('clearable', props.clearable, _this.clearable);
        _this.dark = _this.getInitValue('dark', props.dark, _this.dark);
        _this.dense = _this.getInitValue('dense', props.dense, _this.dense);
        _this.disabled = _this.getInitValue('disabled', props.disabled, _this.disabled);
        _this.hint = _this.getInitValue('hint', props.hint, _this.hint);
        _this.label = _this.getInitValue('label', props.label, _this.label);
        _this.light = _this.getInitValue('light', props.light, _this.light);
        _this.mask = _this.getInitValue('mask', props.mask, _this.mask);
        _this.persistentHint = _this.getInitValue('persistentHint', props.persistentHint, _this.persistentHint);
        _this.placeholder = _this.getInitValue('placeholder', props.placeholder, _this.placeholder);
        _this.readonly = _this.getInitValue('readonly', props.readonly, _this.readonly);
        _this.reverse = _this.getInitValue('reverse', props.reverse, _this.reverse);
        _this.showBorder = _this.getInitValue('showBorder', props.showBorder, _this.showBorder);
        _this.showHelper = _this.getInitValue('showHelper', props.showHelper, _this.showHelper);
        _this.showLabel = _this.getInitValue('showLabel', props.showLabel, _this.showLabel);
        _this.storePath = _this.getInitValue('storePath', props.storePath, _this.storePath);
        _this.value = _this.getInitValue('value', props.value, _this.value);
        _this.validations = _this.getInitValue('validations', props.validations, _this.validations);
        _this.parseValidations(props.validations || {});
        if (_this.parent instanceof Form) {
            _this.parent.registerInput(_this);
        }
        return _this;
    }
    /**
     * Adds parsed validations based on field validations.
     * @param validatons Field validations
     */
    Input.prototype.parseValidations = function (validations) {
        var _this = this;
        Object.keys(validations).forEach(function (name) { return _this.addValidation(name, validations[name]); });
    };
    /**
     * Adds input validation.
     * @param name Validation name
     * @param config Validation config
     */
    Input.prototype.addValidation = function (name, config) {
        var validation = Validation.get(name)(config);
        this.validations[name] = config;
        this.parsedValidations[name] = validation;
        this.updateRules();
    };
    /**
     * Removes input validation.
     * @param name Validation name
     */
    Input.prototype.removeValidation = function (name) {
        delete this.parsedValidations[name];
        this.updateRules();
    };
    /**
     * Updates input rules.
     */
    Input.prototype.updateRules = function () {
        this.rules = Object.values(this.parsedValidations)
            .map(function (validation) { return validation; });
    };
    Object.defineProperty(Input.prototype, "displayValue", {
        /**
         * Input displayed value.
         */
        get: function () {
            return this.formatter(this.value);
        },
        set: function (value) {
            this.value = this.parser(value);
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Retrives a formatted value.
     * @param value Any value
     * @returns A Formatted value
     */
    Input.prototype.formatter = function (value) { return this.formatterFn(value); };
    /**
     * Retrives a parsed value.
     * @param value Any value
     * @returns A parsed value
     */
    Input.prototype.parser = function (value) { return this.parserFn(value); };
    /**
     * Checks the input value are valid to all applied rules.
     * @returns Input is valid
     */
    Input.prototype.isValid = function () {
        var _this = this;
        return this.rules.every(function (rule) { return typeof rule(_this.value) !== 'string'; });
    };
    /**
     * Triggered when the input value changes.
     * @param event DOM event
     * @param element Element clicked
     */
    Input.prototype.change = function (event, element) {
        if (this.events.change) {
            this.events.change({ event: event, element: element, component: this });
        }
    };
    /**
     * Triggered when the data is inputed.
     * @param event DOM event
     * @param element Element focused
     */
    Input.prototype.input = function (event, element) {
        if (this.events.input) {
            this.events.input({ event: event, element: element, component: this });
        }
    };
    /**
     * Triggered when a keyboard key is released.
     * @param event DOM event
     * @param element Element that lost the focus
     */
    Input.prototype.keyup = function (event, element) {
        if (this.events.keyup) {
            this.events.keyup({ event: event, element: element, component: this });
        }
    };
    /**
     * Triggered when the user presses a key on the keyboard.
     * @param event DOM event
     * @param element Element that lost the focus
     */
    Input.prototype.keydown = function (event, element) {
        if (this.events.keydown) {
            this.events.keydown({ event: event, element: element, component: this });
        }
    };
    return Input;
}(ComponentRender));
FormatterParserProvider.registerFormatter('ZdInput', function (value) { return value; });
FormatterParserProvider.registerParser('ZdInput', function (value) { return value; });

var dateDefaults = {
    dateFormat: 'MM-DD-YYYY',
    displayFormat: 'MM-DD-YYYY',
};
/**
 * Base class for Date component.
 */
var Date = /** @class */ (function (_super) {
    __extends(Date, _super);
    /* istanbul ignore next */
    /**
     * Creates a new Date.
     * @param props Date properties
     */
    function Date(props) {
        var _this = _super.call(this, props) || this;
        /**
         * Applies specified color to the control.
         * It can be the name of material or css color in hexa.
         */
        _this.color = 'primary';
        /**
         * Defines the date format.
         */
        _this.dateFormat = dateDefaults.dateFormat;
        /**
         * Defines the date format displayed.
         */
        _this.displayFormat = dateDefaults.displayFormat;
        /**
         * Shows the date picker.
         */
        _this.showDatePicker = false;
        /**
         * Applies the dark theme variant to the date.
         */
        _this.dark = false;
        /**
         * Sets the first day of the week, starting with 0 for Sunday.
         */
        _this.firstDayOfWeek = 0;
        /**
         * Forces 100% width.
         */
        _this.fullWidth = false;
        /**
         * Applies the light theme variant to the date.
         */
        _this.light = false;
        /**
         * Allows changing displayed month with mouse scroll
         */
        _this.scrollable = false;
        /**
         * Toggles visibility of the week numbers in the body of the calendar.
         */
        _this.showWeek = false;
        /**
         * Determines the type of the picker - date for date picker, month for month picker.
         */
        _this.type = 'date';
        /**
         * Width of the picker.
         */
        _this.width = 290;
        _this.isoFormat = 'YYYY-MM-DD';
        _this.dateError = false;
        _this.formatterFn = FormatterParserProvider.getFormatter('ZdDate');
        _this.parserFn = FormatterParserProvider.getParser('ZdDate');
        _this.color = _this.getInitValue('color', props.color, _this.color);
        _this.dark = _this.getInitValue('dark', props.dark, _this.dark);
        _this.dateFormat = _this.getInitValue('dateFormat', props.dateFormat, _this.dateFormat);
        _this.dateValidation = _this.dateValidation.bind(_this);
        _this.displayFormat = _this.getInitValue('displayFormat', props.displayFormat, _this.displayFormat);
        _this.disabled = _this.getInitValue('disabled', props.disabled, _this.disabled);
        _this.firstDayOfWeek = _this.getInitValue('firstDayOfWeek', props.firstDayOfWeek, _this.firstDayOfWeek);
        _this.fullWidth = _this.getInitValue('fullWidth', props.fullWidth, _this.fullWidth);
        _this.light = _this.getInitValue('light', props.light, _this.light);
        _this.locale = _this.getInitValue('locale', props.locale, _this.locale);
        _this.readonly = _this.getInitValue('readonly', props.readonly, _this.readonly);
        _this.rules.push(_this.dateValidation);
        _this.scrollable = _this.getInitValue('scrollable', props.scrollable, _this.scrollable);
        _this.showDatePicker = _this.getInitValue('showDatePicker', props.showDatePicker, _this.showDatePicker);
        _this.showWeek = _this.getInitValue('showWeek', props.showWeek, _this.showWeek);
        _this.type = _this.getInitValue('type', props.type, _this.type);
        _this.width = _this.getInitValue('width', props.width, _this.width);
        _this.createAccessors();
        return _this;
    }
    Date.prototype.formatter = function (value) { return this.formatterFn(value, this); };
    Date.prototype.parser = function (value) { return this.parserFn(value, this); };
    Object.defineProperty(Date.prototype, "dateFormatted", {
        get: function () {
            return this.formatter(this.value);
        },
        set: function (newValue) {
            var lastValue = this.value;
            this.dateError = false;
            this.value = this.parser(newValue);
            if (lastValue !== this.value)
                this.change(this.value);
        },
        enumerable: true,
        configurable: true
    });
    Date.prototype.setDateValue = function (displayValue) {
        var lastValue = this.displayValue;
        if (this.isValidDate(displayValue, this.displayFormat)) {
            this.displayValue = displayValue;
            this.dateError = false;
            if (lastValue !== this.displayValue)
                this.change(this.value);
        }
        else {
            if (!displayValue) {
                this.displayValue = displayValue;
                if (lastValue !== this.displayValue)
                    this.change(this.value);
            }
            this.dateError = !!displayValue;
        }
    };
    Date.prototype.getDateMask = function () {
        return this.displayFormat.replace(/[a-z]/gi, '#');
    };
    Date.prototype.isValidDate = function (value, format) {
        return moment(value, format, true).isValid();
    };
    Object.defineProperty(Date.prototype, "isoValue", {
        get: function () {
            return this.formatISODateValue(this.value);
        },
        set: function (newValue) {
            var lastValue = this.value;
            this.dateError = false;
            this.value = this.parseISODateValue(newValue);
            if (lastValue !== this.value)
                this.change(this.value);
        },
        enumerable: true,
        configurable: true
    });
    Date.prototype.formatISODateValue = function (value) {
        return value ? moment(value, this.dateFormat).format(this.isoFormat) : value;
    };
    Date.prototype.parseISODateValue = function (value) {
        return moment(value, this.isoFormat).format(this.dateFormat);
    };
    Date.prototype.dateValidation = function () {
        var message = ('Invalid date');
        return !this.dateError || message;
    };
    Date.prototype.click = function (event) {
        if (this.events.click) {
            this.events.click({ event: event, component: this });
        }
        if (!event.defaultPrevented) {
            this.showDatePicker = !this.showDatePicker;
        }
    };
    Date.prototype.blur = function (event) {
        this.setDateValue(event.target ? event.target.value : '');
        if (this.events.blur) {
            this.events.blur({ event: event, component: this });
        }
    };
    Date.prototype.clear = function () {
        this.value = null;
        this.change(this.value);
    };
    return Date;
}(Input));
FormatterParserProvider.registerFormatter('ZdDate', function (value, _a) {
    var _b = _a === void 0 ? {} : _a, _c = _b.dateFormat, dateFormat = _c === void 0 ? dateDefaults.dateFormat : _c, _d = _b.displayFormat, displayFormat = _d === void 0 ? dateDefaults.displayFormat : _d;
    return value ? moment(value, dateFormat).format(displayFormat) : '';
});
FormatterParserProvider.registerParser('ZdDate', function (value, _a) {
    var _b = _a === void 0 ? {} : _a, _c = _b.dateFormat, dateFormat = _c === void 0 ? dateDefaults.dateFormat : _c, _d = _b.displayFormat, displayFormat = _d === void 0 ? dateDefaults.displayFormat : _d;
    return value ? moment(value, displayFormat).format(dateFormat) : null;
});

/**
 * Base class for Dialog component.
 */
var Dialog = /** @class */ (function (_super) {
    __extends(Dialog, _super);
    /**
     * Creates a new Dialog
     * @param props Dialog structure
     */
    function Dialog(props) {
        var _this = _super.call(this, props) || this;
        /**
         * Buttons collection that will be displayed on dialog footer
         */
        _this.buttons = [];
        /**
         * The maximum width of the content
         */
        _this.maxWidth = 560;
        /**
         * Clicking outside will not dismiss the dialog
         */
        _this.persistent = true;
        /**
         * The text displayed on dialog
         */
        _this.text = '';
        /**
         * The title displayed on dialog
         */
        _this.title = '';
        /**
         * Dialog type (normal, success, error, warning, info)
         */
        _this.type = 'normal';
        /**
         * Default dialog buttons
         * @private
         */
        _this.defaultButtons = [{
                name: 'dialogButton',
                label: 'OK',
                component: 'ZdButton',
                events: { click: function () { return _this.hide(); } },
            }];
        _this.defaultValues = {
            name: _this.name,
            maxWidth: _this.maxWidth,
            persistent: _this.persistent,
            type: _this.type,
            title: _this.title,
            text: _this.text,
        };
        _this.assignDialogProperties(props);
        _this.createAccessors();
        return _this;
    }
    /**
     * Assign all dialog properties
     * @param dialog Dialog structure
     */
    Dialog.prototype.assignDialogProperties = function (dialog) {
        this.buttons = dialog.buttons && dialog.buttons.length ? dialog.buttons : this.defaultButtons;
        this.name = this.getInitValue('name', dialog.name, this.defaultValues.name);
        this.maxWidth = this.getInitValue('maxWidth', dialog.maxWidth, this.defaultValues.maxWidth);
        this.persistent = this.getInitValue('persistent', dialog.persistent, this.defaultValues.persistent);
        this.type = this.getInitValue('type', dialog.type, this.defaultValues.type);
        this.title = this.getInitValue('title', dialog.title, this.defaultValues.title);
        this.text = this.getInitValue('text', dialog.text, this.defaultValues.text);
        this.isVisible = false;
    };
    /**
     * Displays dialog
     */
    Dialog.prototype.show = function () {
        this.isVisible = true;
    };
    /**
     * Hides dialog
     */
    Dialog.prototype.hide = function () {
        this.isVisible = false;
    };
    return Dialog;
}(Component));

/**
 * Base class for Dropdown component.
 */
var Dropdown = /** @class */ (function (_super) {
    __extends(Dropdown, _super);
    /* istanbul ignore next */
    /**
     * Creates a new DropDown.
     * @param props DropDown properties
     */
    function Dropdown(props) {
        var _this = _super.call(this, props) || this;
        /**
         * Applies position absolute to the dropdown.
         */
        _this.absolute = false;
        /**
         * Designates if menu should close on outside-activator click.
         */
        _this.closeOnClick = true;
        /**
         * Designates if menu should close when its content is clicked.
         */
        _this.closeOnContentClick = true;
        /**
         * Disables the menu.
         */
        _this.disabled = false;
        /**
         * Applies position fixed to the dropdown.
         */
        _this.fixed = false;
        /**
         * Offset the menu on the x-axis.
         */
        _this.offsetX = false;
        /**
         * Offset the menu on the x-axis.
         */
        _this.offsetY = false;
        /**
         * Designates whether menu should open on activator click.
         */
        _this.openOnClick = true;
        /**
         * Designates whether menu should open on activator hover.
         */
        _this.openOnHover = false;
        _this.absolute = _this.getInitValue('absolute', props.absolute, _this.absolute);
        _this.activator = _this.getInitValue('activator', props.activator, _this.activator);
        _this.closeOnClick = _this.getInitValue('closeOnClick', props.closeOnClick, _this.closeOnClick);
        _this.closeOnContentClick = _this.getInitValue('closeOnContentClick', props.closeOnContentClick, _this.closeOnContentClick);
        _this.disabled = _this.getInitValue('disabled', props.disabled, _this.disabled);
        _this.fixed = _this.getInitValue('fixed', props.fixed, _this.fixed);
        _this.maxHeight = _this.getInitValue('maxHeight', props.maxHeight, _this.maxHeight);
        _this.minHeight = _this.getInitValue('minHeight', props.minHeight, _this.minHeight);
        _this.maxWidth = _this.getInitValue('maxWidth', props.maxWidth, _this.maxWidth);
        _this.minWidth = _this.getInitValue('minWidth', props.minWidth, _this.minWidth);
        _this.offsetX = _this.getInitValue('offsetX', props.offsetX, _this.offsetX);
        _this.offsetY = _this.getInitValue('offsetY', props.offsetY, _this.offsetY);
        _this.openOnClick = _this.getInitValue('openOnClick', props.openOnClick, _this.openOnClick);
        _this.openOnHover = _this.getInitValue('openOnHover', props.openOnHover, _this.openOnHover);
        _this.value = _this.getInitValue('value', props.value, _this.value);
        _this.createAccessors();
        return _this;
    }
    return Dropdown;
}(ComponentRender));

/**
 * Base class for Footer component.
 */
var Footer = /** @class */ (function (_super) {
    __extends(Footer, _super);
    /* istanbul ignore next */
    /**
     * Creates a new Footer.
     * @param props Footer properties
     */
    function Footer(props) {
        var _this = _super.call(this, props) || this;
        /**
         * Applies position absolute to footer.
         */
        _this.absolute = false;
        /**
         * Designates the footer as part of the application layout.
         */
        _this.app = false;
        /**
         * Render components on the middle of footer.
         */
        _this.centerSlot = [];
        /**
         * Applies specified color to the control. It can be the name of material or css color in hexa.
         */
        _this.color = 'primary';
        /**
         * Applies the dark theme variant to footer.
         */
        _this.dark = false;
        /**
         * Applies position fixed to footer.
         */
        _this.fixed = false;
        /**
         * Sets the height for footer.
         */
        _this.height = 'auto';
        /**
         * Positions the footer inside the application content.
         */
        _this.inset = false;
        /**
         * Render components on the left of footer.
         */
        _this.leftSlot = [];
        /**
         * Applies the light theme variant to the footer.
         */
        _this.light = false;
        /**
         * Remove all padding from the footer.
         */
        _this.padless = false;
        /**
         * Render components on the right of footer.
         */
        _this.rightSlot = [];
        _this.absolute = _this.getInitValue('absolute', props.absolute, _this.absolute);
        _this.app = _this.getInitValue('app', props.app, _this.app);
        _this.centerSlot = props.centerSlot || _this.centerSlot;
        _this.color = _this.getInitValue('color', props.color, _this.color);
        _this.dark = _this.getInitValue('dark', props.dark, _this.dark);
        _this.fixed = _this.getInitValue('fixed', props.fixed, _this.fixed);
        _this.height = _this.getInitValue('height', props.height, _this.height);
        _this.inset = _this.getInitValue('inset', props.inset, _this.inset);
        _this.leftSlot = props.leftSlot || _this.leftSlot;
        _this.light = _this.getInitValue('light', props.light, _this.light);
        _this.maxHeight = _this.getInitValue('maxHeight', props.maxHeight, _this.maxHeight);
        _this.maxWidth = _this.getInitValue('maxWidth', props.maxWidth, _this.maxWidth);
        _this.minHeight = _this.getInitValue('minHeight', props.minHeight, _this.minHeight);
        _this.minWidth = _this.getInitValue('minWidth', props.minWidth, _this.minWidth);
        _this.padless = _this.getInitValue('padless', props.padless, _this.padless);
        _this.rightSlot = props.rightSlot || _this.rightSlot;
        _this.width = _this.getInitValue('width', props.width, _this.width);
        _this.createAccessors();
        return _this;
    }
    Object.defineProperty(Footer.prototype, "namedSlotsIsVisible", {
        /**
         * Retrieves named slots visibility
         */
        get: function () {
            return this.leftSlot.length || this.centerSlot.length || this.rightSlot.length;
        },
        enumerable: true,
        configurable: true
    });
    return Footer;
}(ComponentRender));

/**
 * Base class for Frame component.
 */
var Frame = /** @class */ (function (_super) {
    __extends(Frame, _super);
    /* istanbul ignore next */
    /**
     * Creates a new Frame.
     * @param props Frame properties
     */
    function Frame(props) {
        var _this = _super.call(this, props) || this;
        _this.loading = true;
        _this.local = false;
        _this.metadata = {};
        _this.override = {};
        _this.path = props.path;
        _this.local = props.local === true;
        _this.override = props.override || _this.override;
        _this.getMetadata();
        return _this;
    }
    Frame.prototype.getMetadata = function () {
        return __awaiter(this, void 0, void 0, function () {
            var page;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, , 2, 3]);
                        return [4 /*yield*/, Metadata.get(this.path, this.local)];
                    case 1:
                        page = _a.sent();
                        this.metadata = merge(page, this.override);
                        return [3 /*break*/, 3];
                    case 2:
                        this.loading = false;
                        return [7 /*endfinally*/];
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    return Frame;
}(ComponentRender));

/**
 * Base class for Column.
 */
var Column = /** @class */ (function (_super) {
    __extends(Column, _super);
    /**
     * Creates a new Column.
     * @param props Column properties
     */
    function Column(props) {
        var _this = _super.call(this, props) || this;
        _this.align = 'left';
        _this.label = '';
        _this.maxWidth = '';
        _this.minWidth = '';
        _this.mask = '';
        _this.sortable = true;
        _this.componentProps = {};
        _this.align = _this.getInitValue('align', props.align, _this.align);
        _this.label = _this.getInitValue('label', props.label, _this.label);
        _this.maxWidth = _this.getInitValue('maxWidth', props.maxWidth, _this.maxWidth);
        _this.minWidth = _this.getInitValue('minWidth', props.minWidth, _this.minWidth);
        _this.mask = _this.getInitValue('mask', props.mask, _this.mask);
        _this.type = _this.getInitValue('type', props.type, _this.type);
        _this.sortable = _this.getInitValue('sortable', props.sortable, _this.sortable);
        _this.componentProps = _this.getInitValue('componentProps', props.componentProps, _this.componentProps);
        return _this;
    }
    Column.prototype.formatter = function (value) {
        var formatterFn = FormatterParserProvider.getFormatter(this.componentProps.component);
        if (formatterFn) {
            return formatterFn(value, this.componentProps);
        }
        return value ? Mask.getValueWithMask(value, this.mask) : value;
    };
    return Column;
}(Component));

/**
 * Column not found error
 */
var ColumnNotFoundError = /** @class */ (function () {
    function ColumnNotFoundError(name, parentName) {
        this.message = "Column " + name + " not found on parent " + parentName;
    }
    return ColumnNotFoundError;
}());

/**
 * Base class for Iterable components.
 */
var Iterable = /** @class */ (function (_super) {
    __extends(Iterable, _super);
    /* istanbul ignore next */
    /**
     * Creates a new Iterable.
     * @param props Iterable properties
     */
    function Iterable(props) {
        var _this = _super.call(this, props) || this;
        /**
         * Iterable columns
         */
        _this.columns = [];
        _this.datasource = DatasourceFactory.factory(props.datasource);
        _this.columns = _this.getColumns(props.columns || _this.columns);
        return _this;
    }
    /**
     * Retrieves columns instances
     * @param columns Columns property
     */
    Iterable.prototype.getColumns = function (columns) {
        return columns.map(function (column) { return new Column(column); });
    };
    /**
     * Retrieves a columns by name
     * @param name Column name
     */
    Iterable.prototype.getColumn = function (name) {
        var column = this.columns.find(function (column) { return column.name === name; });
        if (!column) {
            throw new ColumnNotFoundError(name, this.name);
        }
        return column;
    };
    return Iterable;
}(ComponentRender));

/**
 * Base class for Grid column
 */
var GridColumn = /** @class */ (function (_super) {
    __extends(GridColumn, _super);
    /* istanbul ignore next */
    /**
     * Creates a new Grid Column.
     * @param props Grid column properties
     */
    function GridColumn(props) {
        var _this = _super.call(this, props) || this;
        /* Children components to be rendered on action columns */
        _this.children = [];
        /* Editable column */
        _this.editable = false;
        _this.editable = _this.getInitValue('editable', props.editable, _this.editable);
        _this.children = _this.getInitValue('children', props.children, _this.children);
        _this.createAccessors();
        return _this;
    }
    Object.defineProperty(GridColumn.prototype, "childrenProps", {
        /**
         * Children props without grid system.
         */
        get: function () {
            return this.children.map(function (_a) {
                var conditions = _a.conditions, otherAttrs = __rest(_a, ["conditions"]);
                return otherAttrs;
            });
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(GridColumn.prototype, "childrenConditions", {
        /**
         * Grid system for each child.
         */
        get: function () {
            var childrenGrid = {};
            this.children.forEach(function (child) {
                if (child.conditions) {
                    childrenGrid[child.name] = child.conditions;
                }
            });
            return childrenGrid;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Get the conditions function to be applied to each column depending on the row
     * @param conditions Conditions object
     */
    GridColumn.prototype.getConditions = function (conditions) {
        var cond = Event.factory(conditions);
        return function (row) {
            var appliedConditions = {};
            Object.keys(conditions).forEach(function (condition) {
                appliedConditions[condition] = cond[condition](row);
            });
            return appliedConditions;
        };
    };
    /**
     * Get the Column without the conditions object
     * @param component Column component
     */
    GridColumn.prototype.getColumnWithoutConditions = function (component) {
        return __assign(__assign({}, component), { conditions: undefined });
    };
    return GridColumn;
}(Column));

/**
 * Base class for Grid component
 */
var Grid = /** @class */ (function (_super) {
    __extends(Grid, _super);
    /* istanbul ignore next */
    /**
     * Creates a new Grid.
     * @param props Grid properties
     */
    function Grid(props) {
        var _this = _super.call(this, props) || this;
        /**
         * Components that will be rendered on toolbar slot
         */
        _this.toolbarSlot = [];
        /**
         * Toggle checkbox column
         */
        _this.selectable = false;
        /**
         * Define row height
         */
        _this.dense = false;
        /**
         * Defines if the component should fill the entire available height
         */
        _this.fillHeight = false;
        /**
         * Controls footer visibility
         */
        _this.showFooter = true;
        /**
         * Controls header visibility
         */
        _this.showHeader = true;
        /**
         * Show search field
         */
        _this.showSearch = true;
        /**
         * Selected rows
         */
        _this.selectedRows = [];
        /**
         * Editing rows
         */
        _this.editing = false;
        /**
         * Color of the header background
         */
        _this.headerBackground = 'transparent';
        /**
         * Available order types
         * @private
         */
        _this.orderTypes = { asc: 'asc', desc: 'desc' };
        /**
         * Flag to prevent row click
         * @private
         */
        _this.preventRowClick = false;
        /**
         * Edited rows
         * @private
         */
        _this.editedRows = {};
        /**
         * Invalid inline components
         * @private
         */
        _this.invalidComponents = {};
        _this.dense = _this.getInitValue('dense', props.dense, _this.dense);
        _this.selectable = _this.getInitValue('selectable', props.selectable, _this.selectable);
        _this.height = _this.getInitValue('height', props.height, _this.height);
        _this.fillHeight = _this.getInitValue('fillHeight', props.fillHeight, _this.fillHeight);
        _this.maxHeight = _this.getInitValue('maxHeight', props.maxHeight, _this.maxHeight);
        _this.showFooter = _this.getInitValue('showFooter', props.showFooter, _this.showFooter);
        _this.showHeader = _this.getInitValue('showHeader', props.showHeader, _this.showHeader);
        _this.showSearch = _this.getInitValue('showSearch', props.showSearch, _this.showSearch);
        _this.headerBackground = _this.getInitValue('headerBackground', props.headerBackground, _this.headerBackground);
        _this.toolbarSlot = props.toolbarSlot || _this.toolbarSlot;
        _this.datasource.searchIn = _this.columns.map(function (column) { return column.name; });
        _this.createAccessors();
        return _this;
    }
    Object.defineProperty(Grid.prototype, "paginationLength", {
        /**
         * Get pagination length
         */
        get: function () {
            return Math.ceil(this.datasource.total / this.datasource.limit);
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Get Grid columns objects
     * @param columns Grid columns parameter
     */
    Grid.prototype.getColumns = function (columns) {
        return columns.map(function (column) { return new GridColumn(column); });
    };
    /**
     * Changes column order
     * @async
     * @param field Grid column
     */
    Grid.prototype.changeColumnOrder = function (column) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!(column.sortable && !this.editing)) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.sortColumn(column.name)];
                    case 1:
                        _a.sent();
                        _a.label = 2;
                    case 2: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Sorts grid column
     * @param name Column name
     * @private
     */
    Grid.prototype.sortColumn = function (name) {
        var currentOrder = this.datasource.getOrderByColumn(name);
        if (currentOrder) {
            if (currentOrder === this.orderTypes.asc) {
                return this.datasource.addOrder(name, this.orderTypes.desc);
            }
            return this.datasource.removeOrder(name);
        }
        return this.datasource.addOrder(name, this.orderTypes.asc);
    };
    Object.defineProperty(Grid.prototype, "pageText", {
        /**
         * Retrieves text for page items
         */
        get: function () {
            var last = this.datasource.page * this.datasource.limit;
            var lastRow = last > this.datasource.total ? this.datasource.total : last;
            var firstRow = lastRow === 0 ? 0 : last - this.datasource.limit + 1;
            var translatedText = I18n.instance.t('gridPageText', { firstRow: firstRow, lastRow: lastRow, total: this.datasource.total });
            return translatedText === 'gridPageText'
                ? firstRow + " - " + lastRow + " of " + this.datasource.total
                : translatedText;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Dispatches row click event
     * @param row Grid row
     * @param event DOM event
     * @param element DOM Element
     */
    Grid.prototype.rowClick = function (row, event, element) {
        this.datasource.currentRow = row;
        if (this.events.rowClick && !this.preventRowClick && !this.editing) {
            this.events.rowClick({ event: event, element: element, row: row, component: this, column: undefined });
        }
        this.preventRowClick = false;
    };
    /**
     * Enables editing mode if column is editable
     * @param row Grid row
     * @param column Grid column
     * @param event DOM event
     * @param element DOM Element
     */
    Grid.prototype.cellClick = function (row, column, event, element) {
        this.datasource.currentRow = row;
        if (column.editable && !this.editing) {
            this.selectedRows = [];
            this.editing = true;
            this.preventRowClick = true;
            this.datasource.currentRow = row;
            if (this.events.inlineEdit) {
                this.events.inlineEdit({ event: event, element: element, row: row, column: column, component: this });
            }
        }
        else if (this.events.cellClick && !this.editing) {
            this.preventRowClick = !!this.events.cellClick({ event: event, element: element, row: row, column: column, component: this });
        }
    };
    /**
     * Dispatches select/unselect event
     * @param row Grid row
     * @param isSelected Row is selected
     * @param event DOM event
     * @param element DOM Element
     */
    Grid.prototype.selectClick = function (row, isSelected, event, element) {
        if (isSelected && this.events.selected && !this.editing) {
            this.events.selected({ event: event, element: element, row: row, component: this, column: undefined });
        }
        if (!isSelected && this.events.unselected && !this.editing) {
            this.events.unselected({ event: event, element: element, row: row, component: this, column: undefined });
        }
    };
    /**
     * Dispatches select/unselect all rows event
     * @param isSelected Row is selected
     * @param event DOM event
     * @param element DOM Element
     */
    Grid.prototype.selectAllClick = function (isSelected, event, element) {
        if (isSelected && this.events.selectedAll && !this.editing) {
            this.events.selectedAll({ event: event, element: element, component: this, row: undefined, column: undefined });
        }
        if (!isSelected && this.events.unselectedAll && !this.editing) {
            this.events.unselectedAll({ event: event, element: element, component: this, row: undefined, column: undefined });
        }
    };
    /**
     * Returns editable component properties based on the column definition
     * @param column Column definition
     */
    Grid.prototype.getEditableComponent = function (column, row, events) {
        var _this = this;
        var key = row[this.datasource.uniqueKey];
        var compName = column.name + "_editable_" + key;
        var colValue;
        if (this.editedRows[key] && this.editedRows[key].hasOwnProperty(column.name)) {
            colValue = this.editedRows[key][column.name];
        }
        else {
            colValue = row[column.name];
        }
        var compEvents = {};
        if (column.componentProps && column.componentProps.events) {
            compEvents = Event.factory(column.componentProps.events);
        }
        return __assign(__assign({}, column.componentProps), { name: compName, parent: this, align: column.align, showLabel: false, showHelper: false, dense: true, value: colValue, events: __assign(__assign({}, events), { change: function (_a) {
                    var component = _a.component, event = _a.event, element = _a.element;
                    _this.changeEditableComponent(column, row, component.value);
                    if (compEvents.change) {
                        compEvents.change({ component: component, event: event, element: element });
                    }
                }, blur: function (_a) {
                    var event = _a.event, element = _a.element, component = _a.component;
                    _this.checkCompValidity(component);
                    if (compEvents.blur) {
                        compEvents.blur({ component: component, event: event, element: element });
                    }
                } }) });
    };
    Grid.prototype.checkCompValidity = function (component) {
        if (!component.isValid()) {
            var invalidComponent = {};
            invalidComponent[component.name] = true;
            this.invalidComponents = __assign(__assign({}, this.invalidComponents), invalidComponent);
        }
        else {
            delete this.invalidComponents[component.name];
        }
    };
    /**
     * change event of editable componented
     */
    Grid.prototype.changeEditableComponent = function (column, row, value) {
        var key = row[this.datasource.uniqueKey];
        var columnName = column.name;
        var newRow = {};
        newRow[key] = __assign({}, this.editedRows[key]);
        newRow[key].originalRow = row;
        newRow[key][columnName] = value;
        newRow[key][columnName + "_original"] = row[columnName];
        this.editedRows = __assign(__assign({}, this.editedRows), newRow);
    };
    /**
     * Checks if column is edited
     * @param column Column
     * @param row Row
     */
    Grid.prototype.isEdited = function (column, row) {
        var key = row[this.datasource.uniqueKey];
        return this.editedRows[key] &&
            this.editedRows[key][column.name] !== this.editedRows[key][column.name + "_original"];
    };
    /**
     * Checks if column is valid
     * @param column Column
     * @param row Row
     */
    Grid.prototype.isValid = function (column, row) {
        var key = row[this.datasource.uniqueKey];
        var compName = column.name + "_editable_" + key;
        return !this.invalidComponents.hasOwnProperty(compName);
    };
    /**
     * Cancels all edited rows and enable grid components
     */
    Grid.prototype.cancelEditedRows = function () {
        this.editing = false;
        this.editedRows = {};
        this.invalidComponents = {};
    };
    /**
     * Saves all edited rows if they are valid
     * @throws Will throw when it finds an invalid row
     */
    Grid.prototype.saveEditedRows = function () {
        return __awaiter(this, void 0, void 0, function () {
            var response;
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, Promise.all(this.getEditedRows().map(function (row) { return _this.datasource.put(row); }))];
                    case 1:
                        response = _a.sent();
                        this.cancelEditedRows();
                        return [4 /*yield*/, this.datasource.get()];
                    case 2:
                        _a.sent();
                        return [2 /*return*/, response];
                }
            });
        });
    };
    /**
     * Retrieves all edited rows if they are valid
     * @throws Will throw when it finds an invalid row
     */
    Grid.prototype.getEditedRows = function () {
        var editedRows = [];
        for (var key in this.editedRows) {
            var row = __assign(__assign({}, this.editedRows[key].originalRow), this.editedRows[key]);
            delete row.originalRow;
            for (var attr in row) {
                if (row.hasOwnProperty(attr + "_original")) {
                    delete row[attr + "_original"];
                }
            }
            if (Object.keys(this.invalidComponents).length > 0) {
                throw new Error('Invalid rows');
            }
            editedRows.push(row);
        }
        return editedRows;
    };
    return Grid;
}(Iterable));

/**
 * Base class for Header component.
 */
var Header = /** @class */ (function (_super) {
    __extends(Header, _super);
    /* istanbul ignore next */
    /**
     * Creates a new header.
     * @param props Header properties
     */
    function Header(props) {
        var _this = _super.call(this, props) || this;
        /**
         * Applies position absolute to header.
         */
        _this.absolute = false;
        /**
         * Designates the header as part of the application layout.
         */
        _this.app = false;
        /**
         * Render components on the middle of header.
         */
        _this.centerSlot = [];
        /**
         * Applies specified color to the control. It can be the name of material or css color in hexa.
         */
        _this.color = 'primary';
        /**
         * Applies the dark theme variant to header.
         */
        _this.dark = false;
        /**
         * Designates that the application menu positioned on the left is below the header.
         */
        _this.clippedLeft = true;
        /**
         * Designates that the application menu positioned on the right is below the header.
         */
        _this.clippedRight = true;
        /**
         * Reduces the height of the toolbar content to 48px.
         */
        _this.dense = false;
        /**
         * Applies position fixed to header.
         */
        _this.fixed = false;
        /**
         * Sets the height for header.
         */
        _this.height = 'auto';
        /**
         * Render components on the left of header.
         */
        _this.leftSlot = [];
        /**
         * Applies the light theme variant to the header.
         */
        _this.light = false;
        /**
         * Removes internal padding from header.
         */
        _this.padless = false;
        /**
         * Render components on the right of header.
         */
        _this.rightSlot = [];
        _this.absolute = _this.getInitValue('absolute', props.absolute, _this.absolute);
        _this.app = _this.getInitValue('app', props.app, _this.app);
        _this.centerSlot = props.centerSlot || _this.centerSlot;
        _this.clippedLeft = _this.getInitValue('clippedLeft', props.clippedLeft, _this.clippedLeft);
        _this.clippedRight = _this.getInitValue('clippedRight', props.clippedRight, _this.clippedRight);
        _this.color = _this.getInitValue('color', props.color, _this.color);
        _this.dark = _this.getInitValue('dark', props.dark, _this.dark);
        _this.dense = _this.getInitValue('dense', props.dense, _this.dense);
        _this.elevation = _this.getInitValue('elevation', props.elevation, _this.elevation);
        _this.fixed = _this.getInitValue('fixed', props.fixed, _this.fixed);
        _this.height = _this.getInitValue('height', props.height, _this.height);
        _this.leftSlot = props.leftSlot || _this.leftSlot;
        _this.light = _this.getInitValue('light', props.light, _this.light);
        _this.maxHeight = _this.getInitValue('maxHeight', props.maxHeight, _this.maxHeight);
        _this.maxWidth = _this.getInitValue('maxWidth', props.maxWidth, _this.maxWidth);
        _this.minHeight = _this.getInitValue('minHeight', props.minHeight, _this.minHeight);
        _this.minWidth = _this.getInitValue('minWidth', props.minWidth, _this.minWidth);
        _this.padless = _this.getInitValue('padless', props.padless, _this.padless);
        _this.rightSlot = props.rightSlot || _this.rightSlot;
        _this.width = _this.getInitValue('width', props.width, _this.width);
        _this.createAccessors();
        return _this;
    }
    Object.defineProperty(Header.prototype, "namedSlotsIsVisible", {
        /**
         * Retrieves named slots visibility
         */
        get: function () {
            return this.leftSlot.length || this.centerSlot.length || this.rightSlot.length;
        },
        enumerable: true,
        configurable: true
    });
    return Header;
}(ComponentRender));

/**
 * The Image component <code>zd-image</code> is used to show images.
 */
var Image = /** @class */ (function (_super) {
    __extends(Image, _super);
    /* istanbul ignore next */
    /**
     * Creates a new Image.
     * @param props Image properties
     */
    function Image(props) {
        var _this = _super.call(this, props) || this;
        /**
         * Alternative text for the image, if the image cannot be displayed.
         */
        _this.alt = '';
        /**
         * Height of the image, e.g. <samp>\"100px\"</samp> or <samp>\"50%\"</samp>.
         */
        _this.height = '';
        /**
         * Defines how the image should be resized to fit its container.
         *  See <a href=\"https://developer.mozilla.org/en-US/docs/Web/CSS/object-fit\"
         *  alt=\"Object fit in MDN\" target=\"_blank\">MDN Web Docs</a>
         *  for more details.
         */
        _this.objectFit = '';
        /**
         * Source of the image file.
         */
        _this.src = '';
        /**
         * Width of the image, e.g. <samp>\"100px\"</samp> or <samp>\"50%\"</samp>.
         */
        _this.width = '';
        _this.src = _this.getInitValue('src', props.src, _this.src);
        _this.alt = _this.getInitValue('alt', props.alt, _this.alt);
        _this.width = _this.getInitValue('width', props.width, _this.width);
        _this.height = _this.getInitValue('height', props.height, _this.height);
        _this.objectFit = _this.getInitValue('objectFit', props.objectFit, _this.objectFit);
        return _this;
    }
    /**
     * Returns the style attribute for the image component
     */
    Image.prototype.getStyle = function () {
        var width = this.width ? "width: " + this.width + "; " : '';
        var height = this.height ? "height: " + this.height + "; " : '';
        var objectFit = this.objectFit ? "object-fit: " + this.objectFit + ";" : '';
        return width + height + objectFit;
    };
    return Image;
}(ComponentRender));

var ColumnType;
(function (ColumnType) {
    ColumnType[ColumnType["action"] = 1] = "action";
})(ColumnType || (ColumnType = {}));

/**
 * Base class for Login component.
 */
var Login = /** @class */ (function (_super) {
    __extends(Login, _super);
    /* istanbul ignore next */
    function Login(props) {
        var _this = _super.call(this, props) || this;
        /**
         * Value set int the css property background.
         */
        _this.backgroundStyle = '';
        /**
         * Applies specified color to the control.
         * It can be the name of material or css color in hexa.
         */
        _this.color = '';
        /**
         * Applies the dark theme.
         */
        _this.dark = false;
        /**
         * Card position orientation.
         */
        _this.layout = 'center';
        /**
         * Logo displayed in the top of the card.
         */
        _this.logo = '';
        /**
         * Text displayed under the logo.
         */
        _this.logoMessage = '';
        /**
         * Image displayed out of the card.
         */
        _this.poweredByImage = '';
        /**
         * Image displayed in the card.
         */
        _this.poweredByImageCard = '';
        /**
         * Buttons displayed in the bottom of the card.
         */
        _this.socialLogin = [];
        _this.backgroundStyle = _this.getInitValue('backgroundStyle', props.backgroundStyle, _this.backgroundStyle);
        _this.bottomLink = props.bottomLink || _this.bottomLink;
        _this.color = _this.getInitValue('color', props.color, _this.color);
        _this.dark = _this.getInitValue('dark', props.dark, _this.dark);
        _this.layout = _this.getInitValue('layout', props.layout, _this.layout);
        _this.logo = _this.getInitValue('logo', props.logo, _this.logo);
        _this.logoMessage = _this.getInitValue('logoMessage', props.logoMessage, _this.logoMessage);
        _this.poweredByImage = _this.getInitValue('poweredByImage', props.poweredByImage, _this.poweredByImage);
        _this.poweredByImageCard = _this.getInitValue('poweredByImageCard', props.poweredByImageCard, _this.poweredByImageCard);
        _this.socialLogin = props.socialLogin || _this.socialLogin;
        _this.createAccessors();
        return _this;
    }
    return Login;
}(ComponentRender));

/**
 * Login service class to control token and oauth2 params.
 */
var LoginService = /** @class */ (function () {
    function LoginService() {
    }
    /* istanbul ignore next */
    /**
     * Starts all login required params
     */
    LoginService.startSession = function () {
        Interceptor.addRequestSuccess(this.requestSuccessInterceptor);
        Interceptor.addResponseFail(this.responseFailInterceptor);
        this.clientId = this.getClientId();
        this.clientSecret = this.getClientSecret();
        this.invalidateUrl = this.getInvalidateUrl();
        this.loginUrl = this.getLoginUrl();
        this.homeUrl = Config.homeUrl;
        // new EventEmitter('LoginSucceeded');
        // new EventEmitter('LoginFailed');
    };
    /**
     * Deletes the token from cookie and invalidate it
     */
    LoginService.endSession = function () {
        return __awaiter(this, void 0, void 0, function () {
            var token, requestHeader, params;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        token = Cookie.getCookie('token');
                        if (!token) return [3 /*break*/, 2];
                        requestHeader = { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } };
                        params = new URLSearchParams();
                        params.set('token', token);
                        Cookie.deleteCookie('token');
                        return [4 /*yield*/, Http.post(this.invalidateUrl, params.toString(), requestHeader)];
                    case 1:
                        _a.sent();
                        Router.replace(this.loginUrl);
                        _a.label = 2;
                    case 2: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Returns an environment variable
     * @param variable Variable name
     */
    LoginService.getConfigVar = function (variable) {
        if (Config.env && Config.env[variable]) {
            return Config.env[variable];
        }
        throw "Property " + variable + " not found";
    };
    /**
     * Returns client id
     */
    LoginService.getClientId = function () {
        return this.getConfigVar('clientId');
    };
    /**
     * Returns client secret
     */
    LoginService.getClientSecret = function () {
        return this.getConfigVar('clientSecret');
    };
    /**
     * Returns login url
     */
    LoginService.getLoginUrl = function () {
        return this.getConfigVar('loginUrl');
    };
    /**
     * Returns the invalidate url
     */
    LoginService.getInvalidateUrl = function () {
        return this.getConfigVar('invalidateUrl');
    };
    /**
     * Sets the token in the request header
     * @param requestConfig Request configuration
     */
    LoginService.requestSuccessInterceptor = function (requestConfig) {
        var token = Cookie.getCookie('token');
        var currentRoute = Router.getPath();
        if (token) {
            requestConfig.headers = Object.assign(requestConfig.headers, { Authorization: "Bearer " + token });
            if (currentRoute === LoginService.loginUrl) {
                Router.replace(LoginService.homeUrl);
            }
        }
        return requestConfig;
    };
    /**
     * Replace the url to the login page
     * @param error Http error
     */
    LoginService.responseFailInterceptor = function (error) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                if (error.response && error.response.status === 401 && LoginService.loginUrl !== Router.getPath()) {
                    LoginService.redirectUrl = Router.getPath();
                    Router.replace(LoginService.loginUrl);
                }
                throw error;
            });
        });
    };
    /**
     * Return token payload
     * @param token
     */
    LoginService.getTokenPayload = function (token) {
        var base64Url = token.split('.')[1];
        var base64 = base64Url.replace('-', '+').replace('_', '/');
        return JSON.parse(window.atob(base64));
    };
    /**
     * Client identifier to oauth2
     */
    LoginService.clientId = '';
    /**
     * Client secret to oauth2
     */
    LoginService.clientSecret = '';
    /**
     * Configured app home
     */
    LoginService.homeUrl = '';
    /**
     * Endpoit to invalidate the token
     */
    LoginService.invalidateUrl = '';
    /**
     * App login page url
     */
    LoginService.loginUrl = '';
    /**
     * Url to redirect
     */
    LoginService.redirectUrl = '';
    return LoginService;
}());

/**
 * The Login button component <code>zd-login-button</code> is used to handle login with ZdLoginService.
 */
var LoginButton = /** @class */ (function (_super) {
    __extends(LoginButton, _super);
    /* istanbul ignore next */
    function LoginButton(props) {
        var _this = _super.call(this, props) || this;
        /**
         * Grant type of oauth2
         */
        _this.grantType = 'password';
        /**
         * Endpoint used to authentication
         */
        _this.authUrl = '';
        _this.authUrl = (Config.env && Config.env.authUrl) ? Config.env.authUrl : '';
        return _this;
        // this.handleLoginSuccess = this.events.loginSuccess ? () => this.events.loginSuccess() : this.handleLoginSuccess;
        // this.handleLoginFail = this.events.loginFail ? () => this.events.loginFail() : this.handleLoginFail;
    }
    /**
     * Click event
     */
    LoginButton.prototype.click = function () {
        return __awaiter(this, void 0, void 0, function () {
            var parentValue, response, error_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        parentValue = this.parent ? this.parent.value : {};
                        return [4 /*yield*/, this.sendRequest(parentValue)];
                    case 1:
                        response = _a.sent();
                        this.handleLoginSuccess({ response: response, field: this });
                        return [3 /*break*/, 3];
                    case 2:
                        error_1 = _a.sent();
                        return [3 /*break*/, 3];
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Send request to the configured endpoint
     */
    LoginButton.prototype.sendRequest = function (parentValue) {
        return __awaiter(this, void 0, void 0, function () {
            var requestHeader;
            return __generator(this, function (_a) {
                requestHeader = { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } };
                return [2 /*return*/, Http.post(this.authUrl, this.getQueryString(parentValue), requestHeader)];
            });
        });
    };
    /**
     * Returns a parsed query string
     * @param args Params to the query string
     */
    LoginButton.prototype.getQueryString = function (args) {
        var params = new URLSearchParams();
        params.set('grant_type', this.grantType);
        params.set('client_id', LoginService.clientId);
        params.set('client_secret', LoginService.clientSecret);
        for (var key in args) {
            params.set(key, args[key]);
        }
        return params.toString();
    };
    /**
     * Redirect the app to the configured url
     * @param args Response
     */
    LoginButton.prototype.handleLoginSuccess = function (args) {
        if (args.response.status === 200) {
            Cookie.setCookie('token', args.response.data.token);
            LoginService.redirectUrl = LoginService.redirectUrl || LoginService.homeUrl;
            Router.replace(LoginService.redirectUrl);
            // EventBus.emit('LoginSucceeded', [LoginService.getTokenPayload(args.response.data.token)]);
        }
    };
    return LoginButton;
}(Button));

/**
 * Base class for Menu component.
 */
var Menu = /** @class */ (function (_super) {
    __extends(Menu, _super);
    /* istanbul ignore next */
    /**
     * Creates a new Menu.
     * @param props Menu properties
     */
    function Menu(props) {
        var _this = _super.call(this, props) || this;
        /**
         * Defines if the menu is an application menu
         */
        _this.app = false;
        /**
         * Defines if the menu should rests under the application toolbar
         */
        _this.clipped = false;
        /**
         * Applies position: fixed to the menu
         */
        _this.fixed = false;
        /**
         * Defines if the menu should have a visible container
         */
        _this.floating = false;
        /**
         * Applies the dark theme variant to the menu.
         */
        _this.dark = false;
        /**
         * Create the menu with smaller items.
         */
        _this.dense = true;
        /**
         * Create the menu with condensed width. Only icons and no label.
         */
        _this.mini = false;
        /**
         * Defines the width of the menu when in mini state
         */
        _this.miniWidth = 58;
        /**
         * Defines if the menu is mini
         */
        _this.miniState = false;
        /**
         * Defines if the menu is temporary. Temporary menu sits over above the application and uses a overlay div.
         */
        _this.temporary = false;
        /**
         * Array of menu items
         */
        _this.menuItems = [];
        /**
         * Defines if the close event will turn menu to mini
         */
        _this.closeToMini = true;
        /**
         * Defines if the search is shown
         */
        _this.showSearch = false;
        /**
         * Defines if items are local
         */
        _this.isLocal = false;
        /**
         * Defines if the mouse is over the menu
         */
        _this.mouseOver = false;
        /**
         * Defines the menu width in pixels
         */
        _this.width = 256;
        /**
         * Defines the mobile breakpoint in pixels
         */
        _this.mobileBreakpoint = 600;
        /**
         * Array of components to be rendered on the top part of the menu
         */
        _this.topSlot = [];
        /**
         * Object to store all the routes
         */
        _this.routes = {};
        /**
         * Selected path
         */
        _this.selectedPath = '';
        /**
         * Array of selected items
         */
        _this.selectedItems = [];
        /**
         * Defines the url to get menu items
         */
        _this.menuItemsUrl = '';
        _this.app = _this.getInitValue('app', props.app, _this.app);
        _this.clipped = _this.getInitValue('clipped', props.clipped, _this.clipped);
        _this.fixed = _this.getInitValue('fixed', props.fixed, _this.fixed);
        _this.floating = _this.getInitValue('floating', props.floating, _this.floating);
        _this.dark = _this.getInitValue('dark', props.dark, _this.dark);
        _this.dense = _this.getInitValue('dense', props.dense, _this.dense);
        _this.mini = _this.miniState = _this.getInitValue('mini', props.mini, _this.mini);
        _this.miniWidth = _this.getInitValue('miniWidth', props.miniWidth, _this.miniWidth);
        _this.temporary = _this.getInitValue('temporary', props.temporary, _this.temporary);
        _this.menuItems = props.items || _this.menuItems;
        _this.menuItemsUrl = _this.getInitValue('itemsUrl', props.itemsUrl, _this.menuItemsUrl);
        _this.isLocal = _this.getInitValue('isLocal', props.isLocal, _this.isLocal);
        _this.closeToMini = _this.getInitValue('closeToMini', props.closeToMini, _this.closeToMini);
        _this.showSearch = _this.getInitValue('showSearch', props.showSearch, _this.showSearch);
        _this.width = _this.getInitValue('width', props.width, _this.width);
        _this.mobileBreakpoint = _this.getInitValue('mobileBreakpoint', props.mobileBreakpoint, _this.mobileBreakpoint);
        _this.drawer = window.innerWidth > _this.mobileBreakpoint;
        _this.topSlot = props.topSlot || _this.topSlot;
        _this.createAccessors();
        return _this;
    }
    Object.defineProperty(Menu.prototype, "itemsUrl", {
        /**
         * Defines the url to get menu items
         */
        get: function () {
            return this.menuItemsUrl;
        },
        set: function (url) {
            if (this.menuItemsUrl !== url) {
                this.menuItemsUrl = url;
                if (url) {
                    this.loadMetadata(url);
                }
                else {
                    this.items = [];
                    this.routes = {};
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Menu.prototype, "items", {
        /**
         * Menu items
         */
        get: function () {
            return this.menuItems;
        },
        set: function (items) {
            this.menuItems = items;
            this.saveLinkRoutes(items);
            if (this.selectedPath && this.routes[this.selectedPath]) {
                this.selectItem(this.routes[this.selectedPath]);
            }
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Get menu items from a given url
     * @param element Element mounted reference
     */
    Menu.prototype.onMounted = function (element) {
        _super.prototype.onMounted.call(this, element);
        if (this.itemsUrl) {
            this.loadMetadata(this.itemsUrl);
        }
    };
    /**
     * Load the menu metadata
     * @param url url of the json file
     */
    Menu.prototype.loadMetadata = function (url) {
        var _this = this;
        Http.get(Metadata.getMetadataUrl(url, Boolean(this.isLocal)), { baseURL: '' }).then(function (response) {
            _this.items = response.data;
        });
    };
    /**
     * Save all Link routes parent groups
     */
    Menu.prototype.saveLinkRoutes = function (items, parents) {
        var _this = this;
        if (parents === void 0) { parents = []; }
        items.forEach(function (item) {
            if (item.component === 'ZdMenuLink') {
                _this.routes[item.route] = parents.concat([item]);
            }
            else if (item.component === 'ZdMenuGroup') {
                _this.saveLinkRoutes(item.items, parents.concat([item]));
            }
        });
    };
    /**
     * Toggle menu
     */
    Menu.prototype.toggleMenu = function () {
        var isMobile = window.innerWidth <= this.mobileBreakpoint;
        if (this.closeToMini && !isMobile) {
            this.miniState = !this.miniState;
            this.miniState ? this.closeAllMenus() : this.openSelectedTree();
            this.drawer = true;
        }
        else {
            this.drawer = !this.drawer;
        }
    };
    /**
     * Close all menu
     */
    Menu.prototype.closeAllMenus = function () {
        this.items.forEach(function (item) {
            if (item.component === 'ZdMenuGroup') {
                item.opened = false;
                try {
                    Metadata.getInstance(item.name).close();
                }
                catch (error) {
                    // instance not yet created
                }
            }
        });
    };
    /**
     * Saves the selected path
     * @param path Path name
     */
    Menu.prototype.selectPath = function (path) {
        this.selectedPath = path;
        if (this.routes[path]) {
            this.selectItem(this.routes[path]);
        }
    };
    /**
     * Select an item
     * @param item Menu item
     */
    Menu.prototype.selectItem = function (items) {
        this.selectedItems = items;
        this.openSelectedTree();
    };
    /**
     * @param item Menu item
     * @returns Item is selected or not
     */
    Menu.prototype.isSelected = function (item) {
        return this.selectedItems.filter(function (selected) { return selected.name === item.name; }).length > 0;
    };
    /**
     * Sets the prop mouse over
     */
    Menu.prototype.menuMouseEnter = function () {
        var _this = this;
        if (this.miniState) {
            this.openingMenuTimeout = setTimeout(function () { _this.mouseOver = true; _this.openSelectedTree(); }, 200);
        }
    };
    /**
     * Open all selected items
     */
    Menu.prototype.openSelectedTree = function () {
        this.selectedItems.forEach(function (item) {
            if (item.component === 'ZdMenuGroup') {
                item.opened = true;
                try {
                    Metadata.getInstance(item.name).open();
                }
                catch (error) {
                    // instance not yet created
                }
            }
        });
    };
    /**
     * Close all menu items
     */
    Menu.prototype.menuMouseLeave = function () {
        this.mouseOver = false;
        clearTimeout(this.openingMenuTimeout);
        if (this.miniState) {
            this.closeAllMenus();
        }
    };
    /**
     * Responds to Url changes
     */
    Menu.prototype.urlChanged = function (current) {
        this.selectPath(current.path);
    };
    return Menu;
}(ComponentRender));

/**
 * Base class for MenuLink component.
 */
var MenuLink = /** @class */ (function (_super) {
    __extends(MenuLink, _super);
    /* istanbul ignore next */
    /**
     * Creates a new Menu Link.
     * @param props Menu Link properties
     */
    function MenuLink(props) {
        var _this = _super.call(this, props) || this;
        /**
         * Item label
         */
        _this.label = '';
        /**
         * Item icon
         */
        _this.icon = '';
        /**
         * Url to redirect
         */
        _this.route = '';
        _this.label = _this.getInitValue('label', props.label, _this.label);
        _this.icon = _this.getInitValue('icon', props.icon, _this.icon);
        _this.route = _this.getInitValue('route', props.route, _this.route);
        _this.parentMenu = props.parentMenu;
        _this.parentGroup = props.parentGroup;
        _this.createAccessors();
        return _this;
    }
    /**
     * Event triggered when this item is clicked
     */
    MenuLink.prototype.click = function (event) {
        var preventDefault = false;
        if (this.events.click) {
            preventDefault = this.events.click({ event: event, component: this });
        }
        if (preventDefault) {
            event.preventDefault();
        }
    };
    /**
     * @returns item is selected or not
     */
    MenuLink.prototype.isSelected = function () {
        return this.parentMenu ? this.parentMenu.isSelected(this) : false;
    };
    return MenuLink;
}(ComponentRender));

/**
 * Base class for MenuGroup component.
 */
var MenuGroup = /** @class */ (function (_super) {
    __extends(MenuGroup, _super);
    /* istanbul ignore next */
    /**
     * Creates a new Menu Group.
     * @param props Menu Group properties
     */
    function MenuGroup(props) {
        var _this = _super.call(this, props) || this;
        /**
         * item label
         */
        _this.label = '';
        /**
         * Item icon
         */
        _this.icon = '';
        /**
         * Items grouped
         */
        _this.items = [];
        /**
         * Group state
         */
        _this.opened = false;
        _this.label = _this.getInitValue('label', props.label, _this.label);
        _this.icon = _this.getInitValue('icon', props.icon, _this.icon);
        _this.items = props.items;
        _this.opened = _this.getInitValue('opened', props.opened, _this.opened);
        _this.parentMenu = props.parentMenu;
        _this.parentGroup = props.parentGroup;
        _this.createAccessors();
        return _this;
    }
    /**
     * @returns item is selected or not
     */
    MenuGroup.prototype.isSelected = function () {
        return this.parentMenu ? this.parentMenu.isSelected(this) : false;
    };
    /**
     * Open group
     */
    MenuGroup.prototype.open = function () {
        this.opened = true;
    };
    /**
     * Close group
     */
    MenuGroup.prototype.close = function () {
        this.opened = false;
    };
    return MenuGroup;
}(ComponentRender));

/**
 * Base class for MenuSeparator component.
 */
var MenuSeparator = /** @class */ (function (_super) {
    __extends(MenuSeparator, _super);
    /* istanbul ignore next */
    /**
     * Creates a new Menu Separator.
     * @param props Menu Separator properties
     */
    function MenuSeparator(props) {
        return _super.call(this, props) || this;
    }
    return MenuSeparator;
}(ComponentRender));

/**
 * Base class for MenuButton component.
 */
var MenuButton = /** @class */ (function (_super) {
    __extends(MenuButton, _super);
    /* istanbul ignore next */
    /**
     * Creates a new Menu Button.
     * @param props Menu Button properties
     */
    function MenuButton(props) {
        var _this = _super.call(this, props) || this;
        /**
         * Makes the background transparent (equal to text prop in Vuetify).
         */
        _this.flat = true;
        /**
         * Designates the button as icon, round and flat.
         */
        _this.icon = true;
        /**
         * Designates the button icon, that will be rendered before the label.
         */
        _this.iconName = 'mdi-menu';
        /**
         * Name of the menu component controlled by this button.
         */
        _this.menuName = '';
        _this.flat = _this.getInitValue('flat', props.flat, _this.flat);
        _this.icon = _this.getInitValue('icon', props.icon, _this.icon);
        _this.iconName = _this.getInitValue('iconName', props.iconName, _this.iconName);
        _this.menuName = _this.getInitValue('menuName', props.menuName, _this.menuName);
        _this.events.click = function () {
            var menu = Metadata.getInstance(_this.menuName);
            if (menu) {
                menu.toggleMenu();
            }
        };
        _this.createAccessors();
        return _this;
    }
    return MenuButton;
}(Button));

var textInputDefaults = {
    valueWithPrefix: false,
    prefix: '',
    valueWithSuffix: false,
    suffix: '',
    mask: '',
};
/**
 * Base class for Text component.
 */
var TextInput = /** @class */ (function (_super) {
    __extends(TextInput, _super);
    /* istanbul ignore next */
    /**
     * Create a new TextInput.
     * @param props TextInput properties
     */
    function TextInput(props) {
        var _this = _super.call(this, props) || this;
        /**
         * Appends an icon inside text input component.
         */
        _this.appendIcon = '';
        /**
         * Appends an icon outside text input component.
         */
        _this.appendOuterIcon = '';
        /**
         * Displays a prefix text inside text input component. Overrides prependIcon prop.
         */
        _this.prefix = textInputDefaults.prefix;
        /**
         * Prepends an icon inside text input component.
         */
        _this.prependIcon = '';
        /**
         * Prepends an icon outside text input component.
         */
        _this.prependOuterIcon = '';
        /**
         * Displays a suffix text inside text input component. Overrides appendIcon prop.
         */
        _this.suffix = textInputDefaults.suffix;
        /**
         * Sets input type.
         */
        _this.type = '';
        /**
         * Defines text input value should concat the prefix text.
         */
        _this.valueWithPrefix = textInputDefaults.valueWithPrefix;
        /**
         * Defines text input value should concat the suffix text.
         */
        _this.valueWithSuffix = textInputDefaults.valueWithSuffix;
        _this.formatterFn = FormatterParserProvider.getFormatter('ZdTextInput');
        _this.parserFn = FormatterParserProvider.getParser('ZdTextInput');
        _this.appendIcon = _this.getInitValue('appendIcon', props.appendIcon, _this.appendIcon);
        _this.appendOuterIcon = _this.getInitValue('appendOuterIcon', props.appendOuterIcon, _this.appendOuterIcon);
        _this.prefix = _this.getInitValue('prefix', props.prefix, _this.prefix);
        _this.prependIcon = _this.getInitValue('prependIcon', props.prependIcon, _this.prependIcon);
        _this.prependOuterIcon = _this.getInitValue('prependOuterIcon', props.prependOuterIcon, _this.prependOuterIcon);
        _this.suffix = _this.getInitValue('suffix', props.suffix, _this.suffix);
        _this.type = props.type || _this.type;
        _this.valueWithPrefix = _this.getInitValue('valueWithPrefix', props.valueWithPrefix, _this.valueWithPrefix);
        _this.valueWithSuffix = _this.getInitValue('valueWithSuffix', props.valueWithSuffix, _this.valueWithSuffix);
        _this.createAccessors();
        return _this;
    }
    /**
     * Retrives a formatted value without valueSuffix and with mask.
     * @param value Text value
     * @returns Text formatted value
     */
    TextInput.prototype.formatter = function (value) { return this.formatterFn(value, this); };
    /**
     * Retrives a parsed value with prefix and suffix if it is needed and without mask.
     * @param value Text value
     * @returns Text parsed value
     */
    TextInput.prototype.parser = function (value) { return this.parserFn(value, this); };
    /**
     * Triggered when the appended icon is clicked.
     * @param event DOM event
     * @param element Text input
     */
    TextInput.prototype.appendIconClick = function (event, element) {
        if (this.events.appendIconClick) {
            this.events.appendIconClick({ event: event, element: element, component: this });
        }
    };
    /**
     * Triggered when the appended outer icon is clicked.
     * @param event DOM event
     * @param element Text input
     */
    TextInput.prototype.appendOuterIconClick = function (event, element) {
        if (this.events.appendOuterIconClick) {
            this.events.appendOuterIconClick({ event: event, element: element, component: this });
        }
    };
    /**
     * Triggered when the prepended icon is clicked.
     * @param event DOM event
     * @param element Text input
     */
    TextInput.prototype.prependIconClick = function (event, element) {
        if (this.events.prependIconClick) {
            this.events.prependIconClick({ event: event, element: element, component: this });
        }
    };
    /**
     * Triggered when the prepended outer icon is clicked.
     * @param event DOM event
     * @param element Text input
     */
    TextInput.prototype.prependOuterIconClick = function (event, element) {
        if (this.events.prependOuterIconClick) {
            this.events.prependOuterIconClick({ event: event, element: element, component: this });
        }
    };
    return TextInput;
}(Input));
FormatterParserProvider.registerFormatter('ZdTextInput', function (value, _a) {
    var _b = _a === void 0 ? {} : _a, _c = _b.valueWithPrefix, valueWithPrefix = _c === void 0 ? textInputDefaults.valueWithPrefix : _c, _d = _b.prefix, prefix = _d === void 0 ? textInputDefaults.prefix : _d, _e = _b.valueWithSuffix, valueWithSuffix = _e === void 0 ? textInputDefaults.valueWithSuffix : _e, _f = _b.suffix, suffix = _f === void 0 ? textInputDefaults.suffix : _f, _g = _b.mask, mask = _g === void 0 ? textInputDefaults.mask : _g;
    var formatted = value;
    if (formatted) {
        formatted = valueWithPrefix ? formatted.substring(prefix.length) : formatted;
        formatted = valueWithSuffix ? formatted.slice(0, -suffix.length) : formatted;
        formatted = mask ? Mask.getValueWithMask(formatted, mask) : formatted;
    }
    return formatted;
});
FormatterParserProvider.registerParser('ZdTextInput', function (value, _a) {
    var _b = _a === void 0 ? {} : _a, _c = _b.valueWithPrefix, valueWithPrefix = _c === void 0 ? textInputDefaults.valueWithPrefix : _c, _d = _b.prefix, prefix = _d === void 0 ? textInputDefaults.prefix : _d, _e = _b.valueWithSuffix, valueWithSuffix = _e === void 0 ? textInputDefaults.valueWithSuffix : _e, _f = _b.suffix, suffix = _f === void 0 ? textInputDefaults.suffix : _f, _g = _b.mask, mask = _g === void 0 ? textInputDefaults.mask : _g;
    var formatted = value;
    if (formatted) {
        var prefixValue = valueWithPrefix ? prefix : '';
        var suffixValue = valueWithSuffix ? suffix : '';
        formatted = mask ? Mask.getValueWithoutMask(formatted) : formatted;
        formatted = prefixValue + formatted + suffixValue;
    }
    return formatted;
});

/**
 * Base class for Number component
 */
var Number = /** @class */ (function (_super) {
    __extends(Number, _super);
    /* istanbul ignore next */
    /**
     * Creates a new number input
     */
    function Number(props) {
        var _this = _super.call(this, props) || this;
        /*
         * Mask definition
         */
        _this.mask = {
            decimalCharacter: ',',
            decimalCharacterAlternative: '.',
            digitGroupSeparator: '.',
            decimalPlaces: 2,
        };
        /*
         * Defines how the field content should be aligned.
         */
        _this.align = 'right';
        _this.align = _this.getInitValue('align', props.align, _this.align);
        _this.mask = _this.getInitValue('mask', props.mask, _this.mask);
        _this.localValue = _this.getInitValue('value', props.value, _this.value);
        _this.createAccessors();
        return _this;
    }
    Object.defineProperty(Number.prototype, "value", {
        get: function () {
            return this.localValue;
        },
        set: function (value) {
            this.setNumberValue(value);
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Retrives a formatted value with mask
     * @param value Any value
     * @returns A Formatted value
     */
    Number.prototype.formatter = function (value) {
        var formatted = value;
        if (this.autoNumericObj) {
            formatted = this.autoNumericObj.getFormatted();
            if (value && (!formatted || this.autoNumericObj.formatOther(value).indexOf(formatted) < 0)) {
                this.autoNumericObj.reformat();
                formatted = this.autoNumericObj.getFormatted();
            }
        }
        return formatted;
    };
    /**
     * Retrives a parsed value without mask
     * @param value Any value
     * @returns A parsed value
     */
    Number.prototype.parser = function (value) {
        return value === '' ? null : value && this.autoNumericObj ? this.autoNumericObj.getNumber() : value;
    };
    /**
     * Update the value of the autoNumericObj
     * @param value value of the field
     */
    Number.prototype.setNumberValue = function (value) {
        if (this.autoNumericObj) {
            if (!value) {
                this.autoNumericObj.setValue('');
            }
            else {
                this.autoNumericObj.rawValue = value.toString();
            }
        }
        this.localValue = value;
    };
    return Number;
}(TextInput));

/**
 * Base class for Row component.
 */
var Row = /** @class */ (function (_super) {
    __extends(Row, _super);
    /* istanbul ignore next */
    /**
     * Creates a new Row.
     * @param props Row properties
     */
    function Row(props) {
        var _this = _super.call(this, props) || this;
        _this.dense = true;
        _this.noGutters = false;
        _this.align = _this.getInitValue('align', props.align, _this.align);
        _this.dense = _this.getInitValue('dense', props.dense, _this.dense);
        _this.justify = _this.getInitValue('justify', props.justify, _this.justify);
        _this.noGutters = _this.getInitValue('noGutters', props.noGutters, _this.noGutters);
        _this.createAccessors();
        return _this;
    }
    return Row;
}(ComponentRender));

/**
 * Base class for Select component.
 */
var Select = /** @class */ (function (_super) {
    __extends(Select, _super);
    /* istanbul ignore next */
    /**
     * Create a new Select.
     * @param props Select properties
     */
    function Select(props) {
        var _this = _super.call(this, props) || this;
        /**
         * Field used to display values
         */
        _this.dataText = '';
        /**
         * Field used to save values
         */
        _this.dataValue = '';
        /**
         * Defines if field value should be an object
         */
        _this.returnObject = false;
        /**
         * Enables search input
         */
        _this.autocomplete = true;
        /**
         * Prevents load select data when the input is focused
         */
        _this.preventLoadOnFocus = false;
        /**
         * Preserves configured datasource
         */
        _this.defaultDatasource = {};
        _this.fieldValue = null;
        _this.initialized = false;
        _this.dataText = _this.getInitValue('dataText', props.dataText, _this.dataText);
        _this.dataValue = _this.getInitValue('dataValue', props.dataValue, _this.dataValue);
        _this.returnObject = _this.getInitValue('returnObject', props.returnObject, _this.returnObject);
        _this.autocomplete = _this.getInitValue('autocomplete', props.autocomplete, _this.autocomplete);
        _this.preventLoadOnFocus = _this.getInitValue('preventLoadOnFocus', props.preventLoadOnFocus, _this.preventLoadOnFocus);
        if (props.datasource && props.datasource.type === 'simple') {
            _this.dataText = _this.dataValue = 'value';
        }
        _this.defaultDatasource = _this.getDefaultDatasource(props);
        _this.datasource = DatasourceFactory.factory(_this.defaultDatasource);
        _this.setDefaultValue(props.value, props.datasource && props.datasource.lazyLoad);
        _this.createAccessors();
        _this.initialized = true;
        return _this;
    }
    /**
     * Retrieves datasource structure applying infinity scroll
     * @param field Field structure
     */
    Select.prototype.getDefaultDatasource = function (field) {
        var searchInDefault = this.dataText ? [this.dataText] : [];
        var searchIn = (field.datasource && field.datasource.searchIn || []).concat(searchInDefault);
        return __assign(__assign({}, field.datasource), { searchIn: searchIn, infinityScroll: true, lazyLoad: field.value !== void 0 || (field.datasource && field.datasource.lazyLoad) });
    };
    /**
     * Sets select default value and dispatches defaultAssigned event.
     * @param value Default value
     */
    Select.prototype.setDefaultValue = function (value, lazyLoad) {
        return __awaiter(this, void 0, void 0, function () {
            var pushed;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        pushed = false;
                        if (!(value || typeof value === 'number')) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.setFieldValue(value, false)];
                    case 1:
                        pushed = _a.sent();
                        return [3 /*break*/, 4];
                    case 2:
                        if (!(lazyLoad === false)) return [3 /*break*/, 4];
                        this.preventLoadOnFocus = true;
                        return [4 /*yield*/, this.datasource.get()];
                    case 3:
                        _a.sent();
                        _a.label = 4;
                    case 4:
                        if (!this.preventLoadOnFocus) {
                            this.addLoadEvent();
                        }
                        if (this.events.defaultAssigned) {
                            this.events.defaultAssigned({ pushed: pushed, value: this.selectedData, component: this });
                        }
                        return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Finds and retrieves items searching by value
     * @param value Default value
     */
    Select.prototype.getItemsBySearchValue = function (value, searchIn) {
        return __awaiter(this, void 0, void 0, function () {
            var config, datasource, items;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        config = __assign(__assign({}, this.defaultDatasource), { searchIn: searchIn, search: (value || '').toString(), infinityScroll: false });
                        datasource = DatasourceFactory.factory(config);
                        return [4 /*yield*/, datasource.get()];
                    case 1:
                        items = _a.sent();
                        return [2 /*return*/, items.find(this.getCondition(value))];
                }
            });
        });
    };
    Select.prototype.getCondition = function (filterValue) {
        var _this = this;
        return function (row) { return row[_this.dataValue] === filterValue; };
    };
    /**
     * Adds focus event to load select data and preserves user focus event
     * if it exists.
     */
    Select.prototype.addLoadEvent = function () {
        var _this = this;
        var callback = this.events.focus;
        this.events.focus = function (event) {
            _this.datasource.get();
            _this.events.focus = callback;
            if (callback) {
                callback(event);
            }
        };
    };
    Object.defineProperty(Select.prototype, "selectedData", {
        /**
         * Field selected row
         */
        get: function () {
            return this.datasource.currentRow;
        },
        set: function (row) {
            this.setFieldRowValue(row, false);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Select.prototype, "value", {
        /**
         * Field value
         */
        get: function () {
            return this.fieldValue;
        },
        set: function (value) {
            if (this.initialized && this.value !== value) {
                throw 'Use setValue method to update value on Select';
            }
        },
        enumerable: true,
        configurable: true
    });
    Select.prototype.setValue = function (value) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!(value || typeof value === 'number')) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.setFieldValue(value)];
                    case 1:
                        _a.sent();
                        return [3 /*break*/, 3];
                    case 2:
                        this.fieldValue = null;
                        this.datasource.currentRow = undefined;
                        _a.label = 3;
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Returns the currentRow in the dataText key
     */
    Select.prototype.formatter = function (value) {
        return value ? (typeof value === 'string' ? value : value[this.dataText]) : null;
    };
    Select.prototype.setFieldValue = function (value, callChangeEvent) {
        if (callChangeEvent === void 0) { callChangeEvent = true; }
        return __awaiter(this, void 0, void 0, function () {
            var filterValue, searchIn, searchValue;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        filterValue = this.returnObject ? value[this.dataValue] : value;
                        searchIn = [this.dataValue];
                        return [4 /*yield*/, this.getItemsBySearchValue(filterValue, searchIn)];
                    case 1:
                        searchValue = _a.sent();
                        if (searchValue) {
                            this.setFieldRowValue(searchValue, callChangeEvent);
                            this.preventLoadOnFocus = true;
                            if (this.autocomplete) {
                                this.datasource.data = [searchValue];
                                this.datasource.search = this.selectedData[this.dataText];
                            }
                        }
                        else {
                            this.setFieldRowValue(null, callChangeEvent);
                        }
                        return [2 /*return*/, false];
                }
            });
        });
    };
    /**
     * Set value accessors
     * @param row
     * @param value
     */
    Select.prototype.setFieldRowValue = function (row, callChangeEvent) {
        if (row) {
            var value = this.getValue(row);
            this.fieldValue = value;
        }
        else {
            this.fieldValue = null;
        }
        this.datasource.currentRow = row;
        if (callChangeEvent) {
            this.selectChange(row);
        }
    };
    /**
     * Retrieves input value
     * @param value inputed
     * @private
     */
    Select.prototype.getValue = function (value) {
        return value
            ? this.returnObject ? value : value[this.dataValue]
            : null;
    };
    /**
     * Event trigged when component loses the focus and value changes
     * @param value inputed value
     */
    Select.prototype.selectChange = function (value, event, element) {
        if (this.events.change) {
            this.events.change({ event: event, element: element, value: this.getValue(value), component: this });
        }
    };
    /**
     * Event trigged when value is inputed
     * @param value inputed value
     */
    Select.prototype.selectInput = function (value, event, element) {
        if (this.events.input) {
            this.events.input({ event: event, element: element, value: this.getValue(value), component: this });
        }
    };
    /**
     * Updates search value
     * @param value Search value
     */
    Select.prototype.updateSearch = function (value) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!(value !== this.datasource.search)) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.datasource.setSearch(value)];
                    case 1:
                        _a.sent();
                        _a.label = 2;
                    case 2: return [2 /*return*/];
                }
            });
        });
    };
    return Select;
}(TextInput));

/**
 * Base class for SvgMap component.
 */
var SvgMap = /** @class */ (function (_super) {
    __extends(SvgMap, _super);
    /* istanbul ignore next */
    /**
     * Create a new SVG.
     * @param props SVG properties
     */
    function SvgMap(props) {
        var _this = _super.call(this, props) || this;
        /**
         * Color to fill the map areas
         */
        _this.areaColor = '#bdbdbd';
        /**
         * Color to fill the map areas on hover
         */
        _this.areaHoverColor = '#999';
        /**
         * Color to fill the map areas when selected
         */
        _this.areaSelectColor = 'primary';
        /**
         * Defines the map height. Possible values for width can be
         * <samp>'auto', '100%', '400px' or 400</samp>
         */
        _this.height = 'auto';
        /**
         * Defines the map width. Possible values for width can be
         * <samp>'auto', '100%', '400px' or 400</samp>
         */
        _this.width = 'auto';
        /**
         * SVG file content
         */
        _this.svgContent = '';
        /**
         * Map areas
         * @private
         */
        _this.mapAreas = [];
        _this.srcValue = '';
        _this.selectedAreaValue = '';
        _this.areaColor = _this.getInitValue('areaColor', props.areaColor, _this.areaColor);
        _this.areaHoverColor = _this.getInitValue('areaHoverColor', props.areaHoverColor, _this.areaHoverColor);
        _this.areaSelectColor = _this.getInitValue('areaSelectColor', props.areaSelectColor, _this.areaSelectColor);
        _this.selectedArea = _this.getInitValue('selectedArea', props.selectedArea, _this.selectedArea);
        _this.src = _this.getInitValue('src', props.src, _this.src);
        _this.height = _this.getInitValue('height', props.height, _this.height);
        _this.width = _this.getInitValue('width', props.width, _this.width);
        _this.createAccessors();
        return _this;
    }
    Object.defineProperty(SvgMap.prototype, "src", {
        /**
         * SVG file src
         */
        get: function () {
            return this.srcValue;
        },
        set: function (value) {
            this.srcValue = value;
            this.getSVG();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(SvgMap.prototype, "selectedArea", {
        /**
         * Selected Area
         */
        get: function () {
            return this.selectedAreaValue;
        },
        set: function (value) {
            this.selectedAreaValue = value;
            this.updateSelectedArea();
        },
        enumerable: true,
        configurable: true
    });
    SvgMap.prototype.updateSelectedArea = function () {
        var _this = this;
        this.mapAreas.forEach(function (area) {
            if (area.id === _this.selectedAreaValue) {
                area.classList.add('active');
            }
            else {
                area.classList.remove('active');
            }
        });
    };
    /**
     * Update SVG content
     * @private
     */
    SvgMap.prototype.getSVG = function () {
        var _this = this;
        if (this.srcValue) {
            Http.get(this.srcValue).then(function (response) {
                _this.svgContent = response.data;
                _this.updateSVGContainer();
            });
        }
    };
    /**
     * Update the SVG container
     */
    SvgMap.prototype.updateSVGContainer = function () {
        if (this.svgContainer && this.svgContent) {
            if (this.svgContent.indexOf('<svg') >= 0 && this.svgContent.indexOf('</svg>') >= 0) {
                this.svgContainer.innerHTML = this.svgContent;
                var svgTag = this.svgContainer.getElementsByTagName('svg')[0];
                this.mapAreas = Array.from(svgTag.children);
                this.updateSelectedArea();
            }
            else {
                throw new Error('Must be a valid svg file');
            }
        }
    };
    SvgMap.prototype.mapClick = function (el) {
        this.selectedArea = el.id;
        if (this.events.areaClick) {
            this.events.areaClick({ area: el, component: this });
        }
    };
    return SvgMap;
}(ComponentRender));

/**
 * Base class for Switch component.
 */
var Switch = /** @class */ (function (_super) {
    __extends(Switch, _super);
    /* istanbul ignore next */
    /**
     * Create a new Switch.
     * @param props Switch properties
     */
    function Switch(props) {
        var _this = _super.call(this, props) || this;
        /**
         * Applies specified color to the control.
         * It can be the name of material or css color in hexa.
         */
        _this.color = 'primary';
        /**
         * It can make switch render in inset mode.
         */
        _this.inset = false;
        _this.color = _this.getInitValue('color', props.color, _this.color);
        _this.inset = _this.getInitValue('inset', props.inset, _this.inset);
        return _this;
    }
    return Switch;
}(Input));

/**
 * Base class for Table component.
 */
var Table = /** @class */ (function (_super) {
    __extends(Table, _super);
    /* istanbul ignore next */
    /**
     * Creates a new Table.
     * @param table Table properties
     */
    function Table(props) {
        var _this = _super.call(this, props) || this;
        /**
         * Defines table caption.
         */
        _this.caption = {
            label: '',
            isVisible: false,
            align: 'left',
        };
        /**
         * Controls table width.
         */
        _this.fillWidth = false;
        /**
         * Controls table head visibility.
         */
        _this.showTableHead = true;
        _this.caption = __assign(__assign({}, _this.caption), props.caption || {});
        _this.fillWidth = _this.getInitValue('fillWidth', props.fillWidth, _this.fillWidth);
        _this.showTableHead = _this.getInitValue('showTableHead', props.showTableHead, _this.showTableHead);
        _this.createAccessors();
        return _this;
    }
    return Table;
}(Iterable));

/**
 * Base class for Tab component.
 */
var Tab = /** @class */ (function (_super) {
    __extends(Tab, _super);
    /* istanbul ignore next */
    /**
     * Create a new Tab.
     * @param props Tab properties
     */
    function Tab(props) {
        var _this = _super.call(this, props) || this;
        _this.disabled = false;
        _this.tabTitle = '';
        _this.tabTitle = _this.getInitValue('tabTitle', props.tabTitle, _this.tabTitle);
        _this.disabled = _this.getInitValue('disabled', props.disabled, _this.disabled);
        _this.createAccessors();
        return _this;
    }
    return Tab;
}(Component));

/**
 * Tab not found error
 */
var TabNotFoundError = /** @class */ (function () {
    /* istanbul ignore next */
    /**
     * Create a new Error.
     * @param name Tab name
     * @param parentName Tab parent name
     */
    function TabNotFoundError(name, parentName) {
        this.message = "Tab " + name + " not found on parent " + parentName;
    }
    return TabNotFoundError;
}());

/**
 * Base class for Tabs component.
 */
var Tabs = /** @class */ (function (_super) {
    __extends(Tabs, _super);
    /* istanbul ignore next */
    /**
     * Create a new Tabs.
     * @param props Tabs properties
     */
    function Tabs(props) {
        var _this = _super.call(this, props) || this;
        _this.activeTab = 0;
        _this.tabs = [];
        _this.tabs = _this.getTabs(props.tabs || []);
        _this.activeTab = _this.getInitValue('activeTab', props.activeTab, _this.activeTab);
        _this.createAccessors();
        return _this;
    }
    Tabs.prototype.getTabs = function (tabs) {
        return tabs.map(function (tab) { return new Tab(tab); });
    };
    Tabs.prototype.getTab = function (name) {
        var tab = this.tabs.find(function (tab) { return tab.name === name; });
        if (!tab) {
            throw new TabNotFoundError(name, this.name);
        }
        return tab;
    };
    /**
     * Triggered when active tab changes
     * @param event DOM event
     * @param element Tabs element
     */
    Tabs.prototype.change = function (event, element) {
        if (this.events.change) {
            this.events.change({ event: event, element: element, component: this });
        }
    };
    return Tabs;
}(ComponentRender));

/**
 * Base class for Tag component.
 */
var Tag = /** @class */ (function (_super) {
    __extends(Tag, _super);
    /* istanbul ignore next */
    /**
     * Creates a new Tag.
     * @param props Tag properties
     */
    function Tag(props) {
        var _this = _super.call(this, props) || this;
        _this.tag = '';
        _this.tag = _this.getInitValue('tag', props.tag, _this.tag);
        _this.createAccessors();
        return _this;
    }
    return Tag;
}(ComponentRender));

/**
 * Base class for Text component.
 */
var Text = /** @class */ (function (_super) {
    __extends(Text, _super);
    /* istanbul ignore next */
    /**
     * Creates a new Text.
     * @param props Text properties
     */
    function Text(props) {
        var _this = _super.call(this, props) || this;
        _this.compile = false;
        _this.tag = 'p';
        _this.text = [];
        _this.compile = props.compile === true;
        _this.tag = _this.getInitValue('tag', props.tag, _this.tag);
        _this.text = _this.getInitValue('text', props.text, _this.text);
        _this.createAccessors();
        return _this;
    }
    return Text;
}(Component));

/**
 * Base class for Textarea component.
 */
var Textarea = /** @class */ (function (_super) {
    __extends(Textarea, _super);
    /* istanbul ignore next */
    /**
     * Create a new Textarea.
     * @param props Textarea properties
     */
    function Textarea(props) {
        var _this = _super.call(this, props) || this;
        /**
         * Automatically grow the textarea depending on amount of text.
         */
        _this.autoGrow = false;
        /**
         * Enables autofocus.
         */
        _this.autoFocus = false;
        /**
         * Changes the background-color of the input.
         */
        _this.backgroundColor = '';
        /**
         * Applied when using clearable and the input is dirty.
         */
        _this.clearIcon = '$clear';
        /**
         * Applies the alternate filled input style.
         */
        _this.filled = false;
        /**
         * Designates input type as full-width.
         */
        _this.fullWidth = false;
        /**
         * Displays linear progress bar.
         */
        _this.loading = false;
        /**
         * Remove resize handle.
         */
        _this.resize = false;
        /**
         * Reverses the input orientation.
         */
        _this.reverse = false;
        /**
         * Adds a border radius to the input.
         */
        _this.rounded = false;
        /**
         * Height value for each row. Requires the use of the auto-grow prop.
         */
        _this.rowHeight = 24;
        /**
         * Default row count.
         */
        _this.rows = 5;
        _this.autoGrow = _this.getInitValue('autoGrow', props.autoGrow, _this.autoGrow);
        _this.autoFocus = _this.getInitValue('autoFocus', props.autoFocus, _this.autoFocus);
        _this.backgroundColor = _this.getInitValue('backgroundColor', props.backgroundColor, _this.backgroundColor);
        _this.clearIcon = _this.getInitValue('clearIcon', props.clearIcon, _this.clearIcon);
        _this.counter = _this.getInitValue('counter', props.counter, _this.counter);
        _this.filled = _this.getInitValue('filled', props.filled, _this.filled);
        _this.fullWidth = _this.getInitValue('fullWidth', props.fullWidth, _this.fullWidth);
        _this.loading = _this.getInitValue('loading', props.loading, _this.loading);
        _this.resize = _this.getInitValue('resize', props.resize, _this.resize);
        _this.reverse = _this.getInitValue('reverse', props.reverse, _this.reverse);
        _this.rounded = _this.getInitValue('rounded', props.rounded, _this.reverse);
        _this.rowHeight = _this.getInitValue('rowHeight', props.rowHeight, _this.rowHeight);
        _this.rows = _this.getInitValue('rows', props.rows, _this.rows);
        _this.createAccessors();
        return _this;
    }
    return Textarea;
}(TextInput));

/**
 * Base class for Password component.
 */
var Password = /** @class */ (function (_super) {
    __extends(Password, _super);
    /* istanbul ignore next */
    /**
     * Creates a new Password.
     * @param props Password properties
     */
    function Password(props) {
        var _this = _super.call(this, props) || this;
        /**
        * Sets password input type.
        */
        _this.type = 'password';
        _this.appendIcon = 'mdi-eye-off';
        return _this;
    }
    /**
     * Triggered when the appended icon is clicked.
     * @param event DOM event
     * @param element Password input
     */
    Password.prototype.appendIconClick = function (event, element) {
        if (this.type === 'password') {
            this.type = 'text';
            this.appendIcon = 'mdi-eye';
        }
        else {
            this.type = 'password';
            this.appendIcon = 'mdi-eye-off';
        }
        _super.prototype.appendIconClick.call(this, event, element);
    };
    return Password;
}(TextInput));
FormatterParserProvider.registerFormatter('ZdPassword', function (value) {
    return (value || '').replace(/./g, '*');
});

/**
 * Base class for Checkbox component.
 */
var Checkbox = /** @class */ (function (_super) {
    __extends(Checkbox, _super);
    /* istanbul ignore next */
    /**
     * Create a new Checkbox.
     * @param props Checkbox properties
     */
    function Checkbox(props) {
        var _this = _super.call(this, props) || this;
        /**
         * Applies specified color to the control.
         * It can be the name of material or css color in hexa.
         */
        _this.color = 'primary';
        _this.showHelper = false;
        _this.color = _this.getInitValue('color', props.color, _this.color);
        return _this;
    }
    return Checkbox;
}(Input));

/**
 * Alert Service Class
 */
var AlertService = /** @class */ (function () {
    function AlertService() {
    }
    /**
     * Displays an alert.
     * If has an opened alert it will be closed.
     * @param alert Alert structure
     */
    AlertService.show = function (alert) {
        var _this = this;
        if (this.instance.isVisible) {
            this.hide();
            setTimeout(function () { return _this.display(alert); }, 200);
        }
        else {
            this.display(alert);
        }
    };
    /**
     * Hides current alert
     */
    AlertService.hide = function () {
        this.instance.hide();
    };
    /**
     * Displays an alert
     * @param alert Alert Structure
     */
    AlertService.display = function (alert) {
        this.instance.assignAlertProperties(alert);
        this.instance.show();
    };
    /**
     * Current alert
     */
    AlertService.instance = new Alert({ name: 'alert-instance' });
    return AlertService;
}());

/**
 * Dialog Service Class
 */
var DialogService = /** @class */ (function () {
    function DialogService() {
    }
    /**
     * Displays a dialog.
     * If has an opened dialog it will be closed.
     * @param dialog dialog structure
     */
    DialogService.show = function (dialog) {
        var _this = this;
        if (this.instance.isVisible) {
            this.hide();
            setTimeout(function () { return _this.display(dialog); }, 200);
        }
        else {
            this.display(dialog);
        }
    };
    /**
     * Hides current dialog
     */
    DialogService.hide = function () {
        this.instance.hide();
    };
    /**
     * Displays a dialog
     * @param dialog Dialog structure
     * @private
     */
    DialogService.display = function (dialog) {
        this.instance.assignDialogProperties(dialog);
        this.instance.show();
    };
    DialogService.instance = new Dialog({ name: 'dialog-instance' });
    return DialogService;
}());

/**
 * Modal service class
 */
var ModalService = /** @class */ (function () {
    function ModalService() {
    }
    /**
     * Creates a new modal instance
     * @param modal modal structure
     */
    ModalService.create = function (modal) {
        var instance = new Modal(modal);
        this.modals.push(instance);
        return instance;
    };
    /**
     * Creates a new modal instance from JSON
     * @param modal modal structure
     */
    ModalService.createFromJson = function (modalPath, local) {
        return __awaiter(this, void 0, void 0, function () {
            var modal;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, Metadata.get(modalPath, local)];
                    case 1:
                        modal = _a.sent();
                        return [2 /*return*/, this.create(modal)];
                }
            });
        });
    };
    /**
     * Deletes modal
     * @param modal modal instance
     */
    ModalService.delete = function (modal) {
        var index = this.modals.findIndex(function (m) {
            return m.name === modal.name;
        });
        if (index !== -1) {
            this.modals.splice(index, 1);
        }
    };
    /**
     * Closes last modal;
     */
    ModalService.closeByName = function (name) {
        if (this.modals.length) {
            var modal = this.modals.filter(function (m) { return m.name === name; });
            if (modal.length) {
                modal[0].hide();
            }
        }
    };
    /**
     * Modals collection
     */
    ModalService.modals = [];
    return ModalService;
}());

/**
 * Base class for Modal component.
 */
var Modal = /** @class */ (function (_super) {
    __extends(Modal, _super);
    /**
     * Creates a new modal
     * @param props Modal structure
     */
    function Modal(props) {
        var _this = _super.call(this, props) || this;
        /**
         * Sets the modal's title
         */
        _this.title = '';
        /**
         * If <code>true</code> the modal will open as fullscreen
         */
        _this.fullscreen = false;
        /**
         * If <code>true</code> the modal won't close when overlay is clicked
         */
        _this.persistent = false;
        _this.title = _this.getInitValue('title', props.title, _this.title);
        _this.fullscreen = _this.getInitValue('fullscreen', props.fullscreen, _this.fullscreen);
        _this.persistent = _this.getInitValue('persistent', props.persistent, _this.persistent);
        _this.isVisible = false;
        _this.createAccessors();
        return _this;
    }
    /**
     * Removes modal from modals collection
     */
    Modal.prototype.destroy = function () {
        ModalService.delete(this);
    };
    /**
     * Displays modal
     */
    Modal.prototype.show = function () {
        this.isVisible = true;
    };
    /**
     * Closes modal
     */
    Modal.prototype.hide = function () {
        this.isVisible = false;
    };
    return Modal;
}(Component));

/**
 * Base class for Modal Close Button component.
 */
var ModalCloseButton = /** @class */ (function (_super) {
    __extends(ModalCloseButton, _super);
    /* istanbul ignore next */
    /**
     * Creates a new modal close button
     * @param props Modal close button structure
     */
    function ModalCloseButton(props) {
        var _this = _super.call(this, props) || this;
        /**
         * Name of Modal component to be closed
         */
        _this.modalName = '';
        /**
         * Makes the background transparent (equal to text prop in Vuetify).
         */
        _this.flat = true;
        /**
         * Designates the button as icon, round and flat.
         */
        _this.icon = true;
        /**
         * Designates the button icon, that will be rendered before the label.
         */
        _this.iconName = 'mdi-close';
        _this.modalName = _this.getInitValue('modalName', props.modalName, _this.modalName);
        _this.flat = _this.getInitValue('flat', props.flat, _this.flat);
        _this.icon = _this.getInitValue('icon', props.icon, _this.icon);
        _this.iconName = _this.getInitValue('iconName', props.iconName, _this.iconName);
        _this.events.click = function () {
            ModalService.closeByName(_this.modalName);
        };
        _this.createAccessors();
        return _this;
    }
    return ModalCloseButton;
}(Button));

var defaultTheme = {
    dark: false,
    options: {
        customProperties: true,
        minifyTheme: function (css) {
            return css.replace(/[\r\n|\r|\n]/g, '');
        },
    },
    themes: {
        light: {
            primary: '#772583',
            secondary: '#a0a0a0',
            accent: '#a08ca0',
            error: '#FF5252',
            info: '#2196F3',
            success: '#4CAF50',
            warning: '#FFC107',
            anchor: '#772583',
            grey: '#6a6a6a',
        },
        dark: {
            primary: '#ae5ab9',
            secondary: '#a0a0a0',
            accent: '#FF4081',
            error: '#FF5252',
            info: '#2196F3',
            success: '#4CAF50',
            warning: '#FB8C00',
            anchor: '#772583',
            grey: '#6a6a6a',
        },
    },
    zeedhi: {
        light: {
            // font
            'font-color': 'var(--v-grey-darken2)',
        },
        dark: {
            // font
            'font-color': 'var(--v-grey-lighten3)',
        },
        variables: {
            'default-padding': '16px',
            'font-headline-size': '20px',
            'font-headline-weight': '400',
            'font-title-size': '18px',
            'font-title-weight': '400',
            'font-body1-size': '13px',
            'font-body1-weight': '400',
            'font-body2-size': '13px',
            'font-body2-weight': '500',
            'font-body3-size': '14px',
            'font-body3-weight': '400',
            'font-body4-size': '14px',
            'font-body4-weight': '500',
            'font-caption-size': '11px',
            'font-caption-weight': '400',
        },
    },
};
function createStyleElement(options) {
    var styleEl = document.createElement('style');
    styleEl.type = 'text/css';
    styleEl.id = 'zeedhi-theme-stylesheet';
    if (options.cspNonce) {
        styleEl.setAttribute('nonce', options.cspNonce);
    }
    document.head.appendChild(styleEl);
    return styleEl;
}
function genZeedhiTheme(props, dark, options) {
    var css = '';
    Object.keys(props.variables).forEach(function (key) {
        css += "  --zd-" + key + ": " + props.variables[key] + ";\n";
    });
    var themeColors = dark ? props.dark : props.light;
    Object.keys(themeColors).forEach(function (key) {
        css += "  --zd-" + key + ": " + themeColors[key] + ";\n";
    });
    css = ":root {\n" + css + "}\n\n";
    var styleEl = document.getElementById('zeedhi-theme-stylesheet');
    if (!styleEl) {
        styleEl = createStyleElement(options);
    }
    styleEl.innerHTML = defaultTheme.options.minifyTheme(css);
}
var initTheme = function (theme) {
    var newTheme = __assign({}, defaultTheme);
    var zeedhiProperties = __assign({}, newTheme.zeedhi);
    delete newTheme.zeedhi;
    if (theme) {
        newTheme.dark = theme.dark !== undefined ? theme.dark : newTheme.dark;
        if (theme.colors) {
            newTheme.themes.light = __assign(__assign({}, newTheme.themes.light), (theme.colors.light || {}));
            newTheme.themes.dark = __assign(__assign({}, newTheme.themes.dark), (theme.colors.dark || {}));
        }
        if (theme.options) {
            newTheme.options = __assign(__assign({}, newTheme.options), theme.options);
        }
        if (theme.zeedhi) {
            zeedhiProperties.light = __assign(__assign({}, zeedhiProperties.light), (theme.zeedhi.light || {}));
            zeedhiProperties.dark = __assign(__assign({}, zeedhiProperties.dark), (theme.zeedhi.dark || {}));
            zeedhiProperties.variables = __assign(__assign({}, zeedhiProperties.variables), (theme.zeedhi.variables || {}));
        }
    }
    genZeedhiTheme(zeedhiProperties, newTheme.dark, newTheme.options);
    return newTheme;
};

export { Alert, AlertService, ApexChart, Breadcrumbs, Button, Card, Checkbox, CodeEditor, Col, Column, ColumnType, Component, ComponentRender, Container, Date, Dialog, DialogService, Dropdown, Footer, Form, Frame, Grid, GridColumn, Header, Image, Input, Iterable, Login, LoginButton, LoginService, Menu, MenuButton, MenuGroup, MenuLink, MenuSeparator, Modal, ModalCloseButton, ModalService, Number, Password, Row, Select, SvgMap, Switch, Tab, Table, Tabs, Tag, Text, TextInput, Textarea, initTheme };
